import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { MentionTextarea } from '../components/MentionTextarea';
import { useUserNames } from '../context/UserNamesContext';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import PersonnelPTO from '../components/PersonnelPTO';
import PersonnelPerformance from '../components/PersonnelPerformance';
import PersonnelCompliance from '../components/PersonnelCompliance';
import PersonnelPromoteToUser from '../components/PersonnelPromoteToUser';
import ScheduleSection from '../components/schedule/ScheduleSection';
import ScheduleEventModal from '../components/schedule/ScheduleEventModal';
import { useFieldDraft } from '../hooks/useFieldDraft';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import DropZone from '../components/DropZone';
import PersonnelRequiredCredentials from '../components/PersonnelRequiredCredentials';
import ComposeModal from '../components/ComposeModal';
import SmsComposeModal from '../components/SmsComposeModal';
import { buildCredentialReminder } from '../utils/credentialReminder';
import {
  ArrowLeft,
  Save,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  Clock,
  FileText,
  MessageSquare,
  Plus,
  Download,
  X,
  User,
  DollarSign,
  Heart,
  GraduationCap,
  AlertCircle,
  Upload,
  CheckCircle,
  XCircle,
  ClipboardList,
  Bell,
  TrendingUp,
  BarChart3,
  Send,
  Building,
  Archive,
  Star,
  Target,
  UserPlus,
  Shield,
  ShieldCheck,
  Linkedin,
  Shirt,
  Eye,
  EyeOff,
  Settings,
  MessageCircle,
  Globe,
  Facebook,
  Instagram,
  History,
  RotateCcw,
  Pencil,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import EditCredentialModal from '../components/EditCredentialModal';
import { BookingLinkPanel, useGcalReturnToast } from '../components/booking/BookingLinkPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PersonnelDetail() {
  const { personnelId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  useGcalReturnToast();
  const fileInputRef = useRef(null);
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [portalLink, setPortalLink] = useState(null);
  const [portalLinkEmailed, setPortalLinkEmailed] = useState(false);
  const [inviteAlreadyActivated, setInviteAlreadyActivated] = useState(false);
  const [signingBusyId, setSigningBusyId] = useState(null);
  const [viewAsLoading, setViewAsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editedPerson, setEditedPerson] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteMentions, setNoteMentions] = useState([]);
  const { resolveName } = useUserNames();
  const [noteType, setNoteType] = useState('General');
  // In-page document preview modal (replaces new-tab navigation).
  const [previewingDoc, setPreviewingDoc] = useState(null); // {previewUrl, downloadUrl, fileName, title}
  const [composePrefill, setComposePrefill] = useState(null); // {to, subject, body} | null
  const [smsTo, setSmsTo] = useState(null); // string | null
  const [emailAttachPrefill, setEmailAttachPrefill] = useState(null); // {document_ids, label} | null
  // Persist the in-progress note to localStorage so a session timeout (or
  // accidental refresh) doesn't wipe what the user just typed.
  const { clearDraft: clearNoteDraft } = useFieldDraft(
    `personnel-note:${personnelId}`,
    newNote,
    setNewNote
  );
  const [addingNote, setAddingNote] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('Other');
  const [expirationDate, setExpirationDate] = useState('');
  const [taxYear, setTaxYear] = useState('');
  const [docCategory, setDocCategory] = useState('all'); // all | credential | tax | other
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [docTypes, setDocTypes] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [timeOffStart, setTimeOffStart] = useState('');
  const [timeOffEnd, setTimeOffEnd] = useState('');
  const [timeOffType, setTimeOffType] = useState('Vacation');
  const [timeOffNotes, setTimeOffNotes] = useState('');
  const [jobDuties, setJobDuties] = useState([]);
  const [visitStats, setVisitStats] = useState({
    ytd_visits: 0,
    avg_visits_per_week: 0,
    avg_missed_last_6_weeks: 0,
    missed_last_upload: 0,
    avg_late_notes_last_6_weeks: 0,
    late_notes_last_upload: 0,
  });
  const [visitsLoading, setVisitsLoading] = useState(false);
  
  // Cities coverage state
  const [citiesCoverage, setCitiesCoverage] = useState({ cities: [], visit_cities_count: 0, manual_cities_count: 0 });
  const [newCity, setNewCity] = useState('');
  const [addingCity, setAddingCity] = useState(false);
  
  // Therapist documents (from credential uploads)
  const [therapistDocuments, setTherapistDocuments] = useState([]);
  const [archivedTherapistDocuments, setArchivedTherapistDocuments] = useState([]);
  // Credentials with an outstanding e-sign request (TB Questionnaire, Skills Checklist, etc.)
  const [credentialRequests, setCredentialRequests] = useState([]);

  // Custom fields
  const [customFields, setCustomFields] = useState([]);
  const [customData, setCustomData] = useState({});
  const [editingLayout, setEditingLayout] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [addFieldTab, setAddFieldTab] = useState('personal');
  const [newFieldForm, setNewFieldForm] = useState({
    name: '', field_type: 'text', description: '', target_tab: 'personal'
  });

  // SSN reveal
  const [ssnRevealed, setSsnRevealed] = useState(false);
  const [ssnFull, setSsnFull] = useState('');
  const [ssnLoading, setSsnLoading] = useState(false);

  // Compensation history
  const [compensationHistory, setCompensationHistory] = useState([]);
  const [showCompForm, setShowCompForm] = useState(false);
  const [compForm, setCompForm] = useState({ rate: '', frequency: 'per_visit', start_date: '', notes: '', type: 'compensation' });

  // Job history
  const [jobHistory, setJobHistory] = useState([]);
  const [showJobHistoryForm, setShowJobHistoryForm] = useState(false);
  const [jobHistoryForm, setJobHistoryForm] = useState({ title: '', department: '', start_date: '', notes: '' });

  // Direct reports
  const [directReports, setDirectReports] = useState([]);
  // All personnel for "reports_to" dropdown
  const [allPersonnel, setAllPersonnel] = useState([]);

  // Onboarding
  const [onboardingData, setOnboardingData] = useState(null);
  const [onboardingTemplates, setOnboardingTemplates] = useState([]);
  const [startingOnboarding, setStartingOnboarding] = useState(false);
  const [selectedWorkerType, setSelectedWorkerType] = useState('W2');
  // Onboarding builder (staff): presets, staff picker, e-sign templates, add-item form
  const [onboardingPacks, setOnboardingPacks] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [esignTemplates, setEsignTemplates] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const emptyOnbItem = { name: '', item_type: 'task', required: true, assignee_type: 'new_hire', assignee_user_id: '', assignee_role: 'HR', credential_type: '', esign_template_id: '', due_mode: 'none', due_date: '', due_offset_days: 3, reminder_days_before: '' };
  const [newOnbItem, setNewOnbItem] = useState(emptyOnbItem);
  const [scheduleItemId, setScheduleItemId] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ due_mode: 'none', due_date: '', due_offset_days: 3, reminder_days_before: '' });
  const [applyPackId, setApplyPackId] = useState('');
  const [sendInvitation, setSendInvitation] = useState(true);

  useEffect(() => {
    fetchMetadata();
    fetchPerson();
    fetchJobDuties();
    fetchCitiesCoverage();
    fetchCustomFields();
    // Load document types for category lookup
    axios.get(`${API}/document-types?active_only=true`)
      .then(res => setDocTypes(res.data || []))
      .catch(() => setDocTypes([]));
  }, [personnelId]);

  // Helper: get category for a doc type name
  const getCategoryForType = (typeName) => {
    if (!typeName) return 'credential';
    const found = docTypes.find(t => t.name.toLowerCase() === typeName.toLowerCase());
    return found?.category || 'credential';
  };

  // Helper: filter docs by current docCategory
  const filterByCategory = (docs) => {
    if (docCategory === 'all') return docs;
    return docs.filter(d => {
      const cat = d.category || getCategoryForType(d.document_type || d.document_name);
      return cat === docCategory;
    });
  };

  // Fetch credentials when person is loaded (for all personnel, not just those with therapist_profile_id)
  useEffect(() => {
    if (person?.id) {
      fetchPersonnelCredentials(person.id);
      fetchCustomData(person.id);
      fetchCompensationHistory();
      fetchJobHistory();
      fetchDirectReports();
      fetchAllPersonnel();
      fetchOnboardingData();
      fetchOnboardingTemplates();
      fetchOnboardingBuilderData();
    }
    if (person?.therapist_profile_id) {
      fetchVisitsAndCalculateStats(person.therapist_profile_id);
      fetchCitiesCoverage(); // Refresh cities when we have therapist profile
    }
  }, [person?.id, person?.therapist_profile_id]);

  const fetchPersonnelCredentials = async (personnelId) => {
    try {
      const [activeResponse, archivedResponse, requestsResponse] = await Promise.all([
        axios.get(`${API}/personnel/${personnelId}/credentials`),
        axios.get(`${API}/personnel/${personnelId}/credentials/archived`),
        axios.get(`${API}/personnel/${personnelId}/credential-requests`).catch(() => ({ data: [] }))
      ]);
      
      setTherapistDocuments(activeResponse.data || []);
      setArchivedTherapistDocuments(archivedResponse.data || []);
      setCredentialRequests(requestsResponse.data || []);
    } catch (error) {
      console.error('Failed to load personnel credentials:', error);
      setTherapistDocuments([]);
      setArchivedTherapistDocuments([]);
      setCredentialRequests([]);
    }
  };

  // Merge and deduplicate ALL credentials from both sources (embedded + credential system)
  // For each type: keep only the one with latest expiration as current, rest go to archived
  const getAllCredentials = () => {
    const embeddedDocs = (person?.documents || []).map(d => ({ ...d, _source: 'embedded' }));
    const credDocs = (therapistDocuments || []).map(d => ({ ...d, _source: 'credential' }));
    const allActive = [...embeddedDocs, ...credDocs];

    // Create a normalized grouping key from the document name
    // This groups "PT License" and "PT License (New)" together
    const normalizeKey = (doc) => {
      const name = (doc.document_name || doc.document_type || 'Other').toLowerCase()
        .replace(/\s*\(new\)|\s*\(old\)|\s*\(updated\)|\s*\(expired\)/gi, '')
        .trim();
      return name;
    };

    // Group by normalized key
    const grouped = {};
    allActive.forEach(doc => {
      const key = normalizeKey(doc);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    });

    const current = [];
    const extraArchived = [];

    Object.values(grouped).forEach(docs => {
      const sorted = [...docs].sort((a, b) => {
        const expA = a.expiration_date ? new Date(a.expiration_date).getTime() : 0;
        const expB = b.expiration_date ? new Date(b.expiration_date).getTime() : 0;
        if (expB !== expA) return expB - expA;
        const upA = a.uploaded_at || a.created_at || '';
        const upB = b.uploaded_at || b.created_at || '';
        return upB > upA ? 1 : upB < upA ? -1 : 0;
      });
      current.push(sorted[0]);
      sorted.slice(1).forEach(doc => extraArchived.push(doc));
    });

    // Combine all archived: backend-archived credentials + embedded archived + dedup extras
    const embeddedArchived = (person?.archived_documents || []).map(d => ({ ...d, _source: 'embedded' }));
    const credArchived = (archivedTherapistDocuments || []).map(d => ({ ...d, _source: 'credential' }));
    const allArchived = [...embeddedArchived, ...credArchived, ...extraArchived];

    return { current, archived: allArchived };
  };

  // Get expiration status for a credential
  const getCredentialStatus = (doc) => {
    if (!doc.expiration_date) return null;
    const now = new Date();
    const exp = new Date(doc.expiration_date);
    const daysUntil = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return { level: 'expired', days: Math.abs(daysUntil), label: `Expired ${Math.abs(daysUntil)} days ago` };
    if (daysUntil <= 14) return { level: 'warning', days: daysUntil, label: `Expiring in ${daysUntil} days` };
    return null;
  };

  const REQUIRED_CREDENTIALS = ['DL', 'Therapy License', 'CPR', 'PL', 'Auto Insurance', 'Contract'];

  // Map of required credential names to all known aliases/variations
  const CREDENTIAL_ALIASES = {
    'DL': ['dl', 'driver\'s license', 'drivers license', 'driver license'],
    'Therapy License': ['therapy license', 'pt license', 'pta license', 'ot license', 'ota license', 'slp license', 'cota license'],
    'CPR': ['cpr', 'cpr certification', 'cpr card', 'bls', 'bls certification'],
    'PL': ['pl', 'professional liability', 'liability insurance', 'malpractice'],
    'Auto Insurance': ['auto insurance', 'car insurance', 'vehicle insurance', 'insurance'],
    'Contract': ['contract', 'agreement', 'employment contract', 'ic agreement'],
  };

  // Get all credential alerts for the name banner (expired, expiring, AND missing)
  const getCredentialAlerts = () => {
    const { current } = getAllCredentials();
    const alerts = [];

    // Check expired/expiring
    current.forEach(doc => {
      const status = getCredentialStatus(doc);
      if (status) {
        alerts.push({ ...status, type: doc.document_type || doc.document_name });
      }
    });

    // Check missing required credentials using alias matching.
    // A requirement is satisfied by a matching credential document OR by a
    // fully-executed e-signature packet whose filed document name matches
    // (e.g. a signed ICA / contractor-paperwork packet satisfies "Contract").
    const matchesAlias = (text, aliases) => {
      const t = (text || '').toLowerCase().trim();
      if (!t) return false;
      return aliases.some(alias => {
        if (alias.length <= 3) {
          // Whole-word match for very short aliases (dl, pl, cpr) so we don't
          // false-positive on substrings like "dl" inside "handled".
          const esc = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return new RegExp(`\\b${esc}\\b`).test(t);
        }
        return t.includes(alias) || alias.includes(t);
      });
    };
    const isExecutedEsign = (d) => d.source === 'esignature' || !!d.source_request_id;
    REQUIRED_CREDENTIALS.forEach(req => {
      const aliases = CREDENTIAL_ALIASES[req] || [req.toLowerCase()];
      const hasIt = current.some(d => (
        matchesAlias(d.document_type || d.document_name, aliases) ||
        (isExecutedEsign(d) && matchesAlias(d.document_name, aliases))
      ));
      if (!hasIt) {
        alerts.push({ level: 'missing', days: 0, label: 'Missing', type: req });
      }
    });

    return alerts;
  };

  const handleRestoreTherapistCredential = async (docId) => {
    if (!window.confirm('Restore this credential to current? If a credential of the same type exists, it will be archived.')) return;
    try {
      await axios.post(`${API}/credentials/${docId}/restore`);
      toast.success('Credential restored');
      if (person?.id) {
        fetchPersonnelCredentials(person.id);
      }
    } catch (error) {
      toast.error('Failed to restore credential');
    }
  };

  const fetchCitiesCoverage = async () => {
    try {
      const response = await axios.get(`${API}/personnel/${personnelId}/cities-coverage`);
      setCitiesCoverage(response.data);
    } catch (error) {
      console.error('Failed to load cities coverage:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/custom-fields?target_page=personnel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomFields(res.data || []);
    } catch (error) {
      console.error('Failed to load custom fields:', error);
    }
  };

  const fetchCustomData = async (recordId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/custom-field-values/personnel/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomData(res.data || {});
    } catch (error) {
      console.error('Failed to load custom data:', error);
    }
  };

  const fetchCompensationHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/personnel/${personnelId}/compensation`, { headers: { Authorization: `Bearer ${token}` } });
      setCompensationHistory(res.data || []);
    } catch (e) { console.error('Failed to load compensation:', e); }
  };

  const fetchJobHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/personnel/${personnelId}/job-history`, { headers: { Authorization: `Bearer ${token}` } });
      setJobHistory(res.data || []);
    } catch (e) { console.error('Failed to load job history:', e); }
  };

  const fetchDirectReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/personnel/${personnelId}/direct-reports`, { headers: { Authorization: `Bearer ${token}` } });
      setDirectReports(res.data || []);
    } catch (e) { console.error('Failed to load direct reports:', e); }
  };

  const fetchAllPersonnel = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/personnel?limit=10000`, { headers: { Authorization: `Bearer ${token}` } });
      setAllPersonnel((res.data?.personnel || res.data || []).filter(p => p.id !== personnelId));
    } catch (e) { console.error('Failed to load personnel list:', e); }
  };

  const fetchOnboardingData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/personnel/${personnelId}/onboarding`, { headers: { Authorization: `Bearer ${token}` } });
      setOnboardingData(res.data);
    } catch (e) { console.error('Failed to load onboarding:', e); }
  };

  const fetchOnboardingTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/onboarding/templates`, { headers: { Authorization: `Bearer ${token}` } });
      setOnboardingTemplates(res.data || []);
    } catch (e) { console.error('Failed to load templates:', e); }
  };

  const fetchOnboardingBuilderData = async () => {
    const token = localStorage.getItem('token');
    const h = { headers: { Authorization: `Bearer ${token}` } };
    try { const r = await axios.get(`${API}/onboarding/packs`, h); setOnboardingPacks(r.data || []); } catch { setOnboardingPacks([]); }
    try { const r = await axios.get(`${API}/users`, h); setStaffUsers(r.data || []); } catch { setStaffUsers([]); }
    try { const r = await axios.get(`${API}/esignature/templates`, h); setEsignTemplates(r.data || []); } catch { setEsignTemplates([]); }
  };

  const handleApplyPack = async () => {
    if (!applyPackId) { toast.error('Choose a preset first'); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/personnel/${personnelId}/onboarding/apply-pack/${applyPackId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || 'Preset applied');
      setApplyPackId('');
      fetchOnboardingData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply preset'); }
  };

  const handleAddOnboardingItem = async () => {
    if (!newOnbItem.name.trim()) { toast.error('Item name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      const payload = { ...newOnbItem };
      if (payload.assignee_type === 'user') {
        const u = staffUsers.find(s => s.id === payload.assignee_user_id);
        payload.assignee_user_name = u ? (u.name || u.email) : '';
      }
      payload.due_offset_days = (payload.due_mode === 'before_hire' || payload.due_mode === 'after_hire')
        ? (payload.due_offset_days === '' || payload.due_offset_days == null ? null : parseInt(payload.due_offset_days, 10)) : null;
      payload.reminder_days_before = payload.reminder_days_before === '' || payload.reminder_days_before == null
        ? null : parseInt(payload.reminder_days_before, 10);
      if (payload.due_mode !== 'fixed') payload.due_date = null;
      await axios.post(`${API}/personnel/${personnelId}/onboarding/items`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Item added');
      setNewOnbItem(emptyOnbItem);
      setShowAddItem(false);
      fetchOnboardingData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add item'); }
  };

  const openScheduleEditor = (item) => {
    setScheduleForm({
      due_mode: item.due_mode || 'none',
      due_date: item.due_date || '',
      due_offset_days: item.due_offset_days ?? 3,
      reminder_days_before: item.reminder_days_before ?? '',
    });
    setScheduleItemId(item.id);
  };

  const handleSaveSchedule = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        due_mode: scheduleForm.due_mode,
        due_date: scheduleForm.due_mode === 'fixed' ? (scheduleForm.due_date || null) : null,
        due_offset_days: (scheduleForm.due_mode === 'before_hire' || scheduleForm.due_mode === 'after_hire')
          ? (scheduleForm.due_offset_days === '' ? null : parseInt(scheduleForm.due_offset_days, 10)) : null,
        reminder_days_before: scheduleForm.reminder_days_before === '' ? null : parseInt(scheduleForm.reminder_days_before, 10),
      };
      await axios.put(`${API}/personnel/${personnelId}/onboarding/items/${itemId}/schedule`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Due date updated');
      setScheduleItemId(null);
      fetchOnboardingData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update due date'); }
  };


  const handleDeleteOnboardingItem = async (itemId) => {
    if (!window.confirm('Remove this onboarding item?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/personnel/${personnelId}/onboarding/items/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Item removed');
      fetchOnboardingData();
    } catch (e) { toast.error('Failed to remove item'); }
  };

  const handleSendOnboardingSignature = async (itemId) => {
    if (signingBusyId) return; // prevent duplicate submissions
    setSigningBusyId(itemId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/personnel/${personnelId}/onboarding/items/${itemId}/sign`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data?.reused ? 'Using the existing signature request (no duplicate created)' : 'Signature request sent to the new hire');
      fetchOnboardingData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send signature request'); }
    finally { setSigningBusyId(null); }
  };

  const handleStartOnboarding = async () => {
    setStartingOnboarding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/personnel/${personnelId}/onboarding/start`, {
        worker_type: selectedWorkerType,
        pack_id: applyPackId || null,
        send_invitation: sendInvitation
      }, { headers: { Authorization: `Bearer ${token}` } });
      const presetName = onboardingPacks.find(p => p.id === applyPackId)?.name;
      const base = presetName
        ? `Onboarding started using "${presetName}".`
        : `Onboarding started for ${selectedWorkerType} worker.`;
      toast.success(res.data.email_sent ? `${base} Invitation email sent!` : base);
      fetchOnboardingData();
      fetchPerson();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start onboarding'); }
    finally { setStartingOnboarding(false); }
  };

  const handleUpdateOnboardingItem = async (itemId, status) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/personnel/${personnelId}/onboarding/items/${itemId}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Item marked as ${status}`);
      fetchOnboardingData();
      // Onboarding completion can auto-flip personnel status Onboarding -> Active
      if (res.data?.activated) {
        toast.success('Onboarding complete — status set to Active');
      }
      fetchPerson();
    } catch (e) { toast.error('Failed to update item'); }
  };

  const handleMarkActive = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/personnel/${personnelId}/mark-active`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status set to Active');
      fetchPerson();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark active'); }
  };

  const handleSeedTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/onboarding/templates/seed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchOnboardingTemplates();
    } catch (e) { toast.error('Failed to seed templates'); }
  };

  const revealSSN = async () => {
    setSsnLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/personnel/${personnelId}/ssn`, { headers: { Authorization: `Bearer ${token}` } });
      setSsnFull(res.data?.ssn || '');
      setSsnRevealed(true);
    } catch (e) { toast.error('Failed to load SSN'); }
    finally { setSsnLoading(false); }
  };

  const saveSSN = async (ssn) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/personnel/${personnelId}/ssn`, { ssn }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('SSN updated');
      setSsnFull(ssn);
    } catch (e) { toast.error('Failed to save SSN'); }
  };

  const addCompensation = async () => {
    if (!compForm.rate || !compForm.start_date) { toast.error('Rate and start date required'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/personnel/${personnelId}/compensation`, compForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Compensation entry added');
      setShowCompForm(false);
      setCompForm({ rate: '', frequency: 'per_visit', start_date: '', notes: '', type: 'compensation' });
      fetchCompensationHistory();
    } catch (e) { toast.error('Failed to add compensation'); }
  };

  const deleteCompensation = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/personnel/${personnelId}/compensation/${entryId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchCompensationHistory();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const addJobHistoryEntry = async () => {
    if (!jobHistoryForm.title || !jobHistoryForm.start_date) { toast.error('Title and start date required'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/personnel/${personnelId}/job-history`, jobHistoryForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Job history entry added');
      setShowJobHistoryForm(false);
      setJobHistoryForm({ title: '', department: '', start_date: '', notes: '' });
      fetchJobHistory();
    } catch (e) { toast.error('Failed to add job history'); }
  };

  const deleteJobHistory = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/personnel/${personnelId}/job-history/${entryId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchJobHistory();
    } catch (e) { toast.error('Failed to delete'); }
  };


  const saveCustomData = async (key, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/custom-field-values/personnel/${personnelId}`, 
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomData(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      toast.error('Failed to save custom field');
    }
  };

  const handleAddCustomField = async () => {
    if (!newFieldForm.name) { toast.error('Field name is required'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/custom-fields`, {
        ...newFieldForm,
        target_page: 'personnel',
        target_tab: addFieldTab,
        source: 'manual'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Custom field added');
      setShowAddFieldModal(false);
      setNewFieldForm({ name: '', field_type: 'text', description: '', target_tab: 'personal' });
      fetchCustomFields();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create field');
    }
  };

  const handleRemoveCustomField = async (fieldId) => {
    if (!window.confirm('Remove this custom field? Data will be preserved.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/custom-fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field removed');
      fetchCustomFields();
    } catch (error) {
      toast.error('Failed to remove field');
    }
  };

  const renderCustomFields = (tabName) => {
    const tabFields = customFields.filter(f => f.target_tab === tabName);
    if (tabFields.length === 0 && !editingLayout) return null;
    return (
      <Card className="p-6 mt-4" data-testid={`custom-fields-${tabName}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Custom Fields</h3>
          {editingLayout && (
            <Button variant="outline" size="sm" onClick={() => { setAddFieldTab(tabName); setNewFieldForm(prev => ({ ...prev, target_tab: tabName })); setShowAddFieldModal(true); }}>
              <Plus size={14} className="mr-1" /> Add Field
            </Button>
          )}
        </div>
        {tabFields.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No custom fields on this tab. Click "Add Field" to create one.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tabFields.map(field => (
              <div key={field.id} className="relative group">
                {editingLayout && (
                  <button onClick={() => handleRemoveCustomField(field.id)} className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10" data-testid={`remove-field-${field.id}`}>
                    ×
                  </button>
                )}
                <p className="text-xs text-muted-foreground mb-1">{field.name}</p>
                {isEditing ? (
                  <Input
                    data-testid={`custom-field-${field.field_key}`}
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                    value={customData[field.field_key] || ''}
                    onChange={e => setCustomData(prev => ({ ...prev, [field.field_key]: e.target.value }))}
                    onBlur={e => saveCustomData(field.field_key, e.target.value)}
                    placeholder={field.placeholder || field.name}
                    className="h-8 text-sm"
                  />
                ) : (
                  <p className="text-sm font-medium">{customData[field.field_key] || '-'}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    );
  };

  const handleAddCity = async () => {
    if (!newCity.trim()) {
      toast.error('Please enter a city name');
      return;
    }
    setAddingCity(true);
    try {
      await axios.post(`${API}/personnel/${personnelId}/cities-coverage`, { city: newCity.trim() });
      toast.success(`Added ${newCity} to coverage area`);
      setNewCity('');
      fetchCitiesCoverage();
    } catch (error) {
      toast.error('Failed to add city');
    } finally {
      setAddingCity(false);
    }
  };

  const handleRemoveCity = async (city) => {
    try {
      await axios.delete(`${API}/personnel/${personnelId}/cities-coverage/${encodeURIComponent(city)}`);
      toast.success(`Removed ${city} from coverage area`);
      fetchCitiesCoverage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove city');
    }
  };

  const fetchJobDuties = async () => {
    try {
      const response = await axios.get(`${API}/personnel/${personnelId}/job-duties`);
      setJobDuties(response.data);
    } catch (error) {
      console.error('Failed to load job duties:', error);
    }
  };

  const fetchVisitsAndCalculateStats = async (profileId) => {
    setVisitsLoading(true);
    try {
      const response = await axios.get(`${API}/visits`, { params: { profile_id: profileId } });
      const visitsData = response.data;
      calculateVisitStats(visitsData);
    } catch (error) {
      console.error('Failed to load visits:', error);
    } finally {
      setVisitsLoading(false);
    }
  };

  const calculateVisitStats = (visitsData) => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);

    // Anchor the "last 6 weeks" window to the most recent visit in the data,
    // not to today's wall clock. Otherwise, if the newest uploaded visits are
    // more than 6 weeks old, every 6-week metric zeroes out even though the
    // person has plenty of visits (YTD still shows them).
    let anchorDate = now;
    const validDates = visitsData
      .map(v => new Date(v.date || v.created_at))
      .filter(d => !isNaN(d.getTime()) && d <= now);
    if (validDates.length > 0) {
      anchorDate = new Date(Math.max(...validDates.map(d => d.getTime())));
    }
    const sixWeeksAgo = new Date(anchorDate.getTime() - 42 * 24 * 60 * 60 * 1000);

    // Task names to exclude from visit counts
    const excludedTaskNames = [
      'discharge summary (pt)', 'discharge summary (ot)', 'discharge summary (st)',
      'nb - supervisory visit (pt)', 'nb - supervisory visit (ot)',
      'nb hha sup visit (pt)', '5 day discharge notice ot', '5 day discharge notice pt',
      '5 day discharge notice st', 'oasis-e1 discharge (non-billable) - ot',
      'oasis-e1 discharge (non-billable) - pt', 'oasis-e1 discharge (non-billable) - st',
      'case conference - nb (pt)', 'case conference - nb (ot)', 'case conference - nb (st)'
    ];

    // Function to calculate visit weight (0, 1, or 2)
    const getVisitWeight = (visit) => {
      const taskName = (visit.task_name || '').toLowerCase();
      const status = (visit.status || '').toLowerCase();
      if (status.includes('missed') || status.includes('mv')) return 0;
      if (excludedTaskNames.some(excluded => taskName === excluded)) return 0;
      if (taskName.includes('oasis')) {
        if (taskName.includes('nb') || taskName.includes('non-billable')) return 0;
        return 2;
      }
      return 1;
    };

    // YTD visits (with weighted counting)
    const ytdVisits = visitsData
      .filter(v => new Date(v.date || v.created_at) >= yearStart)
      .reduce((sum, v) => sum + getVisitWeight(v), 0);

    // Get visits in the 6 weeks up to the anchor (most recent) date
    const last6WeeksVisits = visitsData.filter(v => {
      const d = new Date(v.date || v.created_at);
      return !isNaN(d.getTime()) && d >= sixWeeksAgo && d <= anchorDate;
    });

    // Calculate average visits per week
    const weekBuckets = {};
    last6WeeksVisits.forEach(visit => {
      const weight = getVisitWeight(visit);
      if (weight > 0) {
        const visitDate = new Date(visit.date || visit.created_at);
        const sunday = new Date(visitDate);
        sunday.setDate(visitDate.getDate() - visitDate.getDay());
        const weekKey = sunday.toISOString().split('T')[0];
        weekBuckets[weekKey] = (weekBuckets[weekKey] || 0) + weight;
      }
    });
    const avgVisitsPerWeek = Object.keys(weekBuckets).length > 0
      ? Object.values(weekBuckets).reduce((a, b) => a + b, 0) / Object.keys(weekBuckets).length
      : 0;

    // Calculate missed visits
    const isMissed = (status) => {
      const s = (status || '').toLowerCase();
      return s.includes('missed') || s.includes('mv') || s.includes('no-show') || s.includes('no show');
    };

    const missedWeekBuckets = {};
    last6WeeksVisits.forEach(visit => {
      if (isMissed(visit.status)) {
        const visitDate = new Date(visit.date || visit.created_at);
        const sunday = new Date(visitDate);
        sunday.setDate(visitDate.getDate() - visitDate.getDay());
        const weekKey = sunday.toISOString().split('T')[0];
        missedWeekBuckets[weekKey] = (missedWeekBuckets[weekKey] || 0) + 1;
      }
    });
    const avgMissedLast6Weeks = Object.keys(missedWeekBuckets).length > 0
      ? Object.values(missedWeekBuckets).reduce((a, b) => a + b, 0) / Object.keys(missedWeekBuckets).length
      : 0;

    // Get last upload visits
    const sortedByCreated = [...visitsData].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const lastUploadDate = sortedByCreated[0]?.created_at;
    const lastUploadVisits = visitsData.filter(v => v.created_at === lastUploadDate);
    const missedLastUpload = lastUploadVisits.filter(v => isMissed(v.status)).length;

    // Calculate late notes
    const isLateNote = (status) => {
      const s = (status || '').toLowerCase();
      return !s.includes('complete') && !s.includes('missed') && !s.includes('mv');
    };

    const lateNotesWeekBuckets = {};
    last6WeeksVisits.forEach(visit => {
      if (isLateNote(visit.status)) {
        const visitDate = new Date(visit.date || visit.created_at);
        const sunday = new Date(visitDate);
        sunday.setDate(visitDate.getDate() - visitDate.getDay());
        const weekKey = sunday.toISOString().split('T')[0];
        lateNotesWeekBuckets[weekKey] = (lateNotesWeekBuckets[weekKey] || 0) + 1;
      }
    });
    const avgLateNotesLast6Weeks = Object.keys(lateNotesWeekBuckets).length > 0
      ? Object.values(lateNotesWeekBuckets).reduce((a, b) => a + b, 0) / Object.keys(lateNotesWeekBuckets).length
      : 0;
    const lateNotesLastUpload = lastUploadVisits.filter(v => isLateNote(v.status)).length;

    setVisitStats({
      ytd_visits: ytdVisits,
      avg_visits_per_week: avgVisitsPerWeek,
      avg_missed_last_6_weeks: avgMissedLast6Weeks,
      missed_last_upload: missedLastUpload,
      avg_late_notes_last_6_weeks: avgLateNotesLast6Weeks,
      late_notes_last_upload: lateNotesLastUpload,
    });
  };

  const fetchMetadata = async () => {
    try {
      const [divRes, typeRes, statusRes, posRes] = await Promise.all([
        axios.get(`${API}/personnel/divisions`),
        axios.get(`${API}/personnel/types`),
        axios.get(`${API}/personnel/statuses`),
        axios.get(`${API}/ats/position-types`),
      ]);
      setDivisions(divRes.data.divisions);
      setTypes(typeRes.data.types);
      setStatuses(statusRes.data.statuses);
      setPositionTypes(posRes.data.position_types);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const fetchPerson = async () => {
    try {
      const response = await axios.get(`${API}/personnel/${personnelId}`);
      setPerson(response.data);
      setEditedPerson(response.data);
    } catch (error) {
      toast.error('Failed to load personnel record');
      navigate('/personnel');
    } finally {
      setLoading(false);
    }
  };

  const IDENTITY_LABELS = {
    first_name: 'First name', last_name: 'Last name', discipline: 'Discipline',
    status: 'Status', personnel_type: 'Personnel type', employee_id: 'Employee ID',
    pay_rate: 'Pay rate', pay_type: 'Pay type', hire_date: 'Hire date',
    division: 'Division', department: 'Department',
  };

  const handleSave = async () => {
    // Warn before changing identity/sensitive fields on a record that already
    // has visit history (protects against overwriting the wrong person).
    const changedIdentity = Object.keys(IDENTITY_LABELS).filter(
      (k) => person && editedPerson && (person[k] || '') !== (editedPerson[k] || '')
    );
    if (changedIdentity.length > 0) {
      const visitCount = person?.total_visits || 0;
      const lines = changedIdentity.map(
        (k) => `• ${IDENTITY_LABELS[k]}: "${person[k] || '(empty)'}" → "${editedPerson[k] || '(empty)'}"`
      );
      const warn = `You're changing key identity fields on ${person?.first_name || ''} ${person?.last_name || ''}${visitCount ? ` (who has ${visitCount} visits on record)` : ''}:\n\n${lines.join('\n')}\n\nThis affects their linked visits and credentials. Continue?`;
      if (!window.confirm(warn)) return;
    }
    setSaving(true);
    try {
      const { _aliases_text, ...payload } = editedPerson;
      if (_aliases_text !== undefined) {
        payload.name_aliases = [...new Set(_aliases_text.split(',').map((a) => a.replace(/\s+/g, ' ').trim()).filter(Boolean))];
      }
      await axios.put(`${API}/personnel/${personnelId}`, payload);
      toast.success('Personnel updated successfully');
      setIsEditing(false);
      fetchPerson();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update personnel');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/personnel/${personnelId}/history`);
      setHistoryList(res.data || []);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevert = async (historyId) => {
    if (!window.confirm('Revert this record to the selected version? The current values will be saved to history first, so this can be undone.')) return;
    setRevertingId(historyId);
    try {
      await axios.post(`${API}/personnel/${personnelId}/history/${historyId}/revert`);
      toast.success('Record reverted');
      setShowHistory(false);
      fetchPerson();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to revert');
    } finally {
      setRevertingId(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this personnel record?')) return;
    try {
      await axios.delete(`${API}/personnel/${personnelId}`);
      toast.success('Personnel deleted successfully');
      navigate('/personnel');
    } catch (error) {
      toast.error('Failed to delete personnel');
    }
  };

  const handleViewAs = async () => {
    setViewAsLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Prefer the real linked account (user_id or email). If neither
      // exists, use the synthetic "personnel:<id>" handle so the backend
      // simulates a non-admin employee for us.
      const hasAccount = !!(person?.user_id || person?.email);
      const targetId = hasAccount
        ? (person.user_id || person.email)
        : `personnel:${person.id}`;
      const response = await axios.post(
        `${API}/users/view-as/${encodeURIComponent(targetId)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Store viewing_as info from the response
        localStorage.setItem('viewing_as', JSON.stringify(response.data.viewing_as));

        const simulated = response.data.viewing_as?.simulated;
        toast.success(
          simulated
            ? `Simulating view as ${person.first_name} ${person.last_name} (no real login)`
            : `Now viewing as ${person.first_name} ${person.last_name}`
        );

        // In simulated mode the target has no real permissions, so jump
        // straight to the Employee Home — that's what the user wants to
        // verify. For real users, fall back to the highest available nav
        // page.
        if (simulated) {
          window.location.href = '/home';
          return;
        }

        const navPerms = response.data.viewing_as?.permission_level?.nav_permissions || {};
        let redirectTo = '/home';
        if (navPerms.dashboard === true) redirectTo = '/dashboard';
        else if (navPerms.reports === true) redirectTo = '/reports';
        else if (navPerms.map === true) redirectTo = '/map';
        else if (navPerms.agencies === true) redirectTo = '/agencies';
        else if (navPerms.personnel === true) redirectTo = '/personnel';

        window.location.href = redirectTo;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start view-as mode.');
    } finally {
      setViewAsLoading(false);
    }
  };

  const handleSendInvitation = async (force = false) => {
    if (!person?.email) {
      toast.error('This person has no email address. Add an email first.');
      return;
    }
    const confirmMsg = force
      ? `Re-issue a NEW activation link for ${person.email}? This invalidates any earlier link.`
      : `Send a portal activation link to ${person.email}? This creates their Employee Portal account.`;
    if (!window.confirm(confirmMsg)) return;
    setSendingInvite(true);
    try {
      const response = await axios.post(`${API}/personnel/${personnelId}/send-invitation`);
      toast.success(response.data.message);
      setInviteAlreadyActivated(!!response.data.already_activated);
      if (response.data.activation_link) {
        // Backend always mints a fresh link now — surface it and keep person
        // state in sync so no stale/consumed token is ever shown.
        setPortalLink(response.data.activation_link);
        setPortalLinkEmailed(!!response.data.emailed);
        setPerson((prev) => (prev ? { ...prev, portal_activation_link: response.data.activation_link } : prev));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setAddingNote(true);
    try {
      await axios.post(
        `${API}/personnel/${personnelId}/notes?content=${encodeURIComponent(newNote)}&note_type=${noteType}&mentions=${encodeURIComponent(JSON.stringify(noteMentions))}`
      );
      toast.success('Note added');
      setNewNote('');
      setNoteMentions([]);
      setNoteType('General');
      clearNoteDraft();
      fetchPerson();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/personnel/${personnelId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchPerson();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  // When a file is selected, auto-fill name and store file (don't upload yet)
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    // Auto-fill document name from file name (without extension) if empty
    if (!documentName.trim()) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setDocumentName(nameWithoutExt);
    }
  };

  // Actually upload the selected file
  const handleFileUpload = async () => {
    if (!selectedFile) {
      // No file selected yet — open file picker
      fileInputRef.current?.click();
      return;
    }
    if (!documentName.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      let url = `${API}/personnel/${personnelId}/documents?document_name=${encodeURIComponent(documentName)}&document_type=${documentType}`;
      if (expirationDate) {
        url += `&expiration_date=${expirationDate}`;
      }
      const cat = getCategoryForType(documentType);
      url += `&category=${cat}`;
      if (cat === 'tax' && taxYear) {
        url += `&tax_year=${taxYear}`;
      }
      await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setDocumentName('');
      setDocumentType('Other');
      setExpirationDate('');
      setTaxYear('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchPerson();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (docId, fileName, doc = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/personnel/${personnelId}/documents/${docId}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Format filename: LastName, Discipline, ExpDate (if available)
      let downloadName = fileName;
      if (doc && person) {
        const lastName = person.last_name || '';
        const discipline = person.discipline || '';
        const expDate = doc.expiration_date ? new Date(doc.expiration_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '.') : '';
        const ext = fileName.split('.').pop() || '';
        const docType = doc.document_type || doc.document_name || '';
        downloadName = `${lastName}, ${discipline}${expDate ? ', ' + expDate : ''}${docType ? ' - ' + docType : ''}.${ext}`;
      }
      
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDownloadArchivedDocument = async (docId, fileName, doc = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/personnel/${personnelId}/archived-documents/${docId}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Format filename: LastName, Discipline, ExpDate (if available)
      let downloadName = fileName;
      if (doc && person) {
        const lastName = person.last_name || '';
        const discipline = person.discipline || '';
        const expDate = doc.expiration_date ? new Date(doc.expiration_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '.') : '';
        const ext = fileName.split('.').pop() || '';
        const docType = doc.document_type || doc.document_name || '';
        downloadName = `${lastName}, ${discipline}${expDate ? ', ' + expDate : ''}${docType ? ' - ' + docType : ''}.${ext}`;
      }
      
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download archived document');
    }
  };

  const handleDownloadTherapistDocument = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/documents/${doc.id}/download`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Format filename: LastName, Discipline, ExpDate (exp date)
      const lastName = person?.last_name || '';
      const discipline = person?.discipline || '';
      const expDate = doc.expiration_date ? new Date(doc.expiration_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }).replace(/\//g, '.') : '';
      const ext = (doc.file_name || '').split('.').pop() || 'pdf';
      const docType = doc.document_type || doc.document_name || 'Credential';
      const downloadName = `${lastName}, ${discipline}${expDate ? ', ' + expDate + ' (exp)' : ''} - ${docType}.${ext}`;
      
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download credential');
    }
  };

  const handlePreviewDocument = (doc, isArchived = false) => {
    let previewUrl;
    let downloadUrl;
    if (doc._source === 'embedded' && isArchived) {
      previewUrl = `${API}/personnel/${personnelId}/archived-documents/${doc.id}/preview`;
      downloadUrl = `${API}/personnel/${personnelId}/archived-documents/${doc.id}`;
    } else if (doc._source === 'embedded') {
      previewUrl = `${API}/personnel/${personnelId}/documents/${doc.id}/preview`;
      downloadUrl = `${API}/personnel/${personnelId}/documents/${doc.id}`;
    } else {
      previewUrl = `${API}/documents/${doc.id}/preview`;
      downloadUrl = `${API}/documents/${doc.id}/download`;
    }
    setPreviewingDoc({
      previewUrl,
      downloadUrl,
      fileName: doc.file_name || doc.document_name,
      title: doc.document_name || doc.document_type,
    });
  };

  const handleRestoreArchivedDocument = async (docId) => {
    if (!window.confirm('Restore this document to current documents? If a document of the same type already exists, it will be archived.')) return;
    try {
      await axios.post(`${API}/personnel/${personnelId}/archived-documents/${docId}/restore`);
      toast.success('Document restored to current');
      fetchPerson();
    } catch (error) {
      toast.error('Failed to restore document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/personnel/${personnelId}/documents/${docId}`);
      toast.success('Document deleted');
      fetchPerson();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const [sendingDocId, setSendingDocId] = useState(null);
  const handleSendCredentialToAgencies = async (doc) => {
    setSendingDocId(doc.id);
    try {
      const res = await axios.post(`${API}/credentials/${doc.id}/send-to-agencies`);
      if (res.data?.success) {
        toast.success(res.data.message || 'Sent to agencies');
      } else {
        toast.warning(res.data?.message || 'No matching agencies to send to');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credential');
    } finally {
      setSendingDocId(null);
    }
  };

  // ---- State-board license verification (TX PT/PTA) ----
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyRec, setVerifyRec] = useState(null);      // verification record
  const [verifyType, setVerifyType] = useState('');
  const [verifyExp, setVerifyExp] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const verifyPollRef = useRef(null);

  const pollVerification = (id) => {
    clearInterval(verifyPollRef.current);
    verifyPollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/license-verifications/${id}`);
        setVerifyRec(data);
        if (data.status !== 'processing') {
          clearInterval(verifyPollRef.current);
          setVerifyBusy(false);
          if (data.parsed_expiration) setVerifyExp(data.parsed_expiration);
        }
      } catch (e) {
        clearInterval(verifyPollRef.current);
        setVerifyBusy(false);
      }
    }, 3000);
  };

  const startLicenseVerify = async (doc) => {
    setVerifyType(doc.document_type);
    setVerifyExp('');
    setVerifyRec(null);
    setVerifyBusy(true);
    setVerifyOpen(true);
    try {
      const { data } = await axios.post(`${API}/license-verifications/verify`, {
        personnel_id: personnelId,
        credential_type: doc.document_type,
      });
      setVerifyRec(data);
      if (data.status === 'processing') pollVerification(data.id);
      else setVerifyBusy(false);
    } catch (error) {
      setVerifyBusy(false);
      setVerifyRec({ status: 'error', error: error.response?.data?.detail || 'Verification failed to start' });
    }
  };

  const confirmAndFile = async () => {
    if (!verifyRec?.id) return;
    setVerifyBusy(true);
    try {
      const { data } = await axios.post(`${API}/license-verifications/${verifyRec.id}/confirm`,
        { expiration_date: verifyExp || null });
      toast.success(`Filed ${verifyType}${data.expiration_date ? ` (expires ${data.expiration_date})` : ''}`);
      setVerifyOpen(false);
      fetchPerson();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to file');
    } finally {
      setVerifyBusy(false);
    }
  };

  const dismissVerification = async () => {
    if (verifyRec?.id) {
      try { await axios.post(`${API}/license-verifications/${verifyRec.id}/dismiss`); } catch (e) {}
    }
    clearInterval(verifyPollRef.current);
    setVerifyOpen(false);
  };

  const handleBulkDeleteDocuments = async () => {
    const ids = Array.from(selectedDocIds);
    if (ids.length === 0) {
      toast.error('Select at least one document');
      return;
    }
    if (!window.confirm(`Permanently delete ${ids.length} document${ids.length === 1 ? '' : 's'}? This drops the file from durable storage as well — it cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/documents/bulk-delete`, { document_ids: ids });
      const { deleted, missing } = res.data;
      toast.success(`Deleted ${deleted} document${deleted === 1 ? '' : 's'}${missing ? ` (${missing} missing)` : ''}`);
      setSelectedDocIds(new Set());
      fetchPerson();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete documents');
    }
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const handleBulkDownload = async (docIds) => {
    try {
      const ids = Array.isArray(docIds) ? docIds : [];
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/personnel/${personnelId}/credentials/download-zip`,
        { document_ids: ids },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/zip' });
      // Try to read filename from header; fall back to default
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `credentials_${personnelId}.zip`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const included = response.headers['x-documents-included'];
      const skipped = response.headers['x-documents-skipped'];
      if (skipped && parseInt(skipped) > 0) {
        toast.warning(`Downloaded ${included} document(s); skipped ${skipped} (file missing on server)`);
      } else {
        toast.success(`Downloaded ${included || ids.length || 'all'} document(s)`);
      }
      setSelectedDocIds(new Set());
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Bulk download failed';
      toast.error(msg);
    }
  };

  const handleCompleteReminder = async (dutyId, reminderId) => {
    try {
      await axios.put(`${API}/admin/job-duties/${dutyId}/reminders/${reminderId}/complete`);
      toast.success('Reminder marked as complete');
      fetchJobDuties();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleRequestTimeOff = async () => {
    if (!timeOffStart || !timeOffEnd) {
      toast.error('Please enter start and end dates');
      return;
    }
    try {
      let url = `${API}/personnel/${personnelId}/time-off?start_date=${timeOffStart}&end_date=${timeOffEnd}&type=${timeOffType}`;
      if (timeOffNotes) {
        url += `&notes=${encodeURIComponent(timeOffNotes)}`;
      }
      await axios.post(url);
      toast.success('Time off request submitted');
      setTimeOffStart('');
      setTimeOffEnd('');
      setTimeOffType('Vacation');
      setTimeOffNotes('');
      fetchPerson();
    } catch (error) {
      toast.error('Failed to submit time off request');
    }
  };

  const handleUpdateTimeOffStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/personnel/${personnelId}/time-off/${requestId}?status=${status}`);
      toast.success(`Time off ${status.toLowerCase()}`);
      fetchPerson();
    } catch (error) {
      toast.error('Failed to update time off status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Onboarding':
        return 'bg-blue-100 text-blue-800';
      case 'Inactive':
        return 'bg-gray-100 text-gray-800';
      case 'On Leave':
        return 'bg-yellow-100 text-yellow-800';
      case 'Terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Loading personnel record...
      </div>
    );
  }

  if (!person) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Personnel record not found</p>
        <Button className="mt-4" onClick={() => navigate('/personnel')}>
          Back to Personnel
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/personnel')}
            data-testid="back-btn"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-semibold text-foreground tracking-tight">
              {person.first_name} {person.last_name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <Badge className={getStatusColor(person.status)}>
                {person.status}
              </Badge>
              {person.status === 'Onboarding' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                  onClick={handleMarkActive}
                  data-testid="mark-active-btn"
                >
                  <CheckCircle2 size={14} />
                  Mark Active
                </Button>
              )}
              <span className="text-sm text-muted-foreground">
                {person.division} • {person.personnel_type}
              </span>
            </div>
            {/* Credential expiration alerts */}
            {(() => {
              const alerts = getCredentialAlerts();
              if (alerts.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-2 mt-2">
                  {alerts.map((alert, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        alert.level === 'expired'
                          ? 'bg-red-100 text-red-700'
                          : alert.level === 'missing'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                      data-testid={`credential-alert-${alert.level}`}
                    >
                      <AlertCircle size={12} />
                      {alert.type} — {alert.level === 'expired' ? `expired ${alert.days}d ago` : alert.level === 'missing' ? 'missing' : `expiring in ${alert.days}d`}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditedPerson(person);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              {person?.email && (
                <Button
                  variant="outline"
                  onClick={() => handleSendInvitation(false)}
                  disabled={sendingInvite}
                  className="gap-2"
                  data-testid="send-invite-btn"
                >
                  <Send size={16} />
                  {sendingInvite ? 'Sending...' : 'Send Invitation'}
                </Button>
              )}
              {(person?.work_email || person?.email) && (
                <Button
                  variant="outline"
                  onClick={() => setComposePrefill({
                    to: person.work_email || person.email,
                    subject: '',
                    body: '',
                  })}
                  data-testid="email-person-btn"
                  className="gap-2"
                  title={`Email ${person.work_email || person.email}`}
                >
                  <Mail size={16} />
                  Email
                </Button>
              )}
              {(person?.mobile_phone || person?.phone) && (
                <Button
                  variant="outline"
                  onClick={() => setSmsTo(person.mobile_phone || person.phone)}
                  data-testid="text-person-btn"
                  className="gap-2"
                  title={`Text ${person.mobile_phone || person.phone}`}
                >
                  <MessageCircle size={16} />
                  Text
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                data-testid="edit-btn"
              >
                Edit
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={openHistory}
                  data-testid="history-btn"
                  className="gap-2"
                  title="View change history"
                >
                  <History size={16} />
                  History
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowScheduleModal(true)}
                data-testid="schedule-top-btn"
                className="gap-2"
              >
                <Calendar size={16} />
                Schedule
              </Button>
              <Button
                variant={editingLayout ? "default" : "ghost"}
                size="sm"
                onClick={() => setEditingLayout(!editingLayout)}
                data-testid="edit-layout-btn"
                title="Add or remove custom fields on this page"
              >
                <Settings size={14} className="mr-1" />
                {editingLayout ? 'Done' : 'Layout'}
              </Button>
              <Button
                variant="outline"
                onClick={handleViewAs}
                disabled={viewAsLoading}
                data-testid="view-as-btn"
                title={(person?.user_id || person?.email)
                  ? "View the app as this user"
                  : "Simulate this employee's view (no real account linked)"}
              >
                <Eye size={16} className="mr-2" />
                {viewAsLoading ? 'Loading...' : 'View As'}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="delete-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {inviteAlreadyActivated && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="already-activated-banner">
          <p className="text-xs font-medium text-amber-900">
            Note: this account was already activated. The fresh link below re-issues portal access and resets their temporary password — if they already know their password, they can simply log in instead.
          </p>
        </div>
      )}

      {(portalLink || person?.portal_activation_link) && (
        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="portal-activation-banner">
          <p className="text-xs font-medium text-blue-900 mb-1">
            Portal activation link {portalLink ? (portalLinkEmailed ? '(also emailed to the hire — this is the latest link)' : '(email not connected — share this with the hire)') : ''}
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={portalLink || person?.portal_activation_link}
              className="flex-1 text-xs bg-white border border-blue-200 rounded px-2 py-1.5 text-blue-800"
              data-testid="portal-activation-link"
              onFocus={(e) => e.target.select()}
            />
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-blue-300 text-blue-700"
              data-testid="copy-portal-link-btn"
              onClick={() => {
                navigator.clipboard.writeText(portalLink || person?.portal_activation_link);
                toast.success('Activation link copied');
              }}
            >
              Copy
            </Button>
          </div>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue={person?.therapist_profile_id ? "dashboard" : "personal"} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1">
          {person?.therapist_profile_id && (
            <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="text-xs px-3 py-1.5">
              <BarChart3 size={14} className="mr-1" />
              Dashboard
            </TabsTrigger>
          )}
          <TabsTrigger value="personal" data-testid="tab-personal" className="text-xs px-3 py-1.5">
            <User size={14} className="mr-1" />
            Personal
          </TabsTrigger>
          <TabsTrigger value="job" data-testid="tab-job" className="text-xs px-3 py-1.5">
            <Briefcase size={14} className="mr-1" />
            Job
          </TabsTrigger>
          <TabsTrigger value="duties" data-testid="tab-duties" className="text-xs px-3 py-1.5">
            <ClipboardList size={14} className="mr-1" />
            Duties
          </TabsTrigger>
          <TabsTrigger value="timeoff" data-testid="tab-timeoff" className="text-xs px-3 py-1.5">
            <Calendar size={14} className="mr-1" />
            PTO
          </TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance" className="text-xs px-3 py-1.5">
            <Star size={14} className="mr-1" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance" className="text-xs px-3 py-1.5">
            <GraduationCap size={14} className="mr-1" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="emergency" data-testid="tab-emergency" className="text-xs px-3 py-1.5">
            <AlertCircle size={14} className="mr-1" />
            Emergency
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="text-xs px-3 py-1.5">
            <FileText size={14} className="mr-1" />
            Docs
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes" className="text-xs px-3 py-1.5">
            <MessageSquare size={14} className="mr-1" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="benefits" data-testid="tab-benefits" className="text-xs px-3 py-1.5">
            <Heart size={14} className="mr-1" />
            Benefits
          </TabsTrigger>
          <TabsTrigger value="pay" data-testid="tab-pay" className="text-xs px-3 py-1.5">
            <DollarSign size={14} className="mr-1" />
            Pay
          </TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="text-xs px-3 py-1.5">
            <CheckCircle size={14} className="mr-1" />
            Onboard
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule" className="text-xs px-3 py-1.5">
            <Calendar size={14} className="mr-1" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account" className="text-xs px-3 py-1.5">
            <Shield size={14} className="mr-1" />
            Account
          </TabsTrigger>
        </TabsList>

        {/* Personal Tab */}
        <TabsContent value="personal">
          <Card className="p-6">
            {isEditing ? (
              <div className="space-y-4">
                {/* Names Section */}
                <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2">Basic Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      First Name
                    </label>
                    <Input
                      value={editedPerson.first_name}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, first_name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Last Name
                    </label>
                    <Input
                      value={editedPerson.last_name}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, last_name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Preferred Name / Nickname
                    </label>
                    <Input
                      value={editedPerson.preferred_name || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, preferred_name: e.target.value })
                      }
                      placeholder="Optional"
                    />
                  </div>
                  {isAdmin && (
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-muted-foreground">
                        Also known as / name aliases
                      </label>
                      <Input
                        value={editedPerson._aliases_text ?? (editedPerson.name_aliases || []).join(', ')}
                        onChange={(e) => setEditedPerson({ ...editedPerson, _aliases_text: e.target.value })}
                        placeholder="e.g. Denney Choate, Choate, Dennis — names used in visit uploads"
                        data-testid="personnel-name-aliases-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Comma-separated. Reports and uploads match these names to this person.</p>
                    </div>
                  )}
                </div>

                {/* Contact Info */}
                <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mt-4">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Personal Email
                    </label>
                    <Input
                      type="email"
                      value={editedPerson.email || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, email: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Work Email
                    </label>
                    <Input
                      type="email"
                      value={editedPerson.work_email || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, work_email: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Phone
                    </label>
                    <Input
                      value={editedPerson.phone || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, phone: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Mobile Phone
                    </label>
                    <Input
                      value={editedPerson.mobile_phone || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, mobile_phone: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Address
                  </label>
                  <Input
                    value={editedPerson.address || ''}
                    onChange={(e) =>
                      setEditedPerson({ ...editedPerson, address: e.target.value })
                    }
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      City
                    </label>
                    <Input
                      value={editedPerson.city || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, city: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      State
                    </label>
                    <Input
                      value={editedPerson.state || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, state: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Zip Code
                    </label>
                    <Input
                      value={editedPerson.zip_code || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, zip_code: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* Personal Details */}
                <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mt-4">Personal Details</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Date of Birth
                    </label>
                    <Input
                      type="date"
                      value={editedPerson.date_of_birth || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, date_of_birth: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      SSN
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={editedPerson.ssn_full || ssnFull || ''}
                        onChange={(e) => setEditedPerson({ ...editedPerson, ssn_full: e.target.value })}
                        placeholder="XXX-XX-XXXX"
                        data-testid="ssn-edit-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Gender
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editedPerson.gender || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, gender: e.target.value })
                      }
                    >
                      <option value="">Select...</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Non-Binary">Non-Binary</option>
                      <option value="Prefer Not to Say">Prefer Not to Say</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Marital Status
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editedPerson.marital_status || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, marital_status: e.target.value })
                      }
                    >
                      <option value="">Select...</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Domestic Partnership">Domestic Partnership</option>
                      <option value="Prefer Not to Say">Prefer Not to Say</option>
                    </select>
                  </div>
                </div>

                {/* Additional Info */}
                <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mt-4">Additional Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      LinkedIn Profile
                    </label>
                    <Input
                      value={editedPerson.linkedin_url || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, linkedin_url: e.target.value })
                      }
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Shirt Size
                    </label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={editedPerson.shirt_size || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, shirt_size: e.target.value })
                      }
                    >
                      <option value="">Select...</option>
                      <option value="XS">XS</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                      <option value="2XL">2XL</option>
                      <option value="3XL">3XL</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Dietary Restrictions
                    </label>
                    <Input
                      value={editedPerson.dietary_restrictions || ''}
                      onChange={(e) =>
                        setEditedPerson({ ...editedPerson, dietary_restrictions: e.target.value })
                      }
                      placeholder="e.g., Vegetarian, Gluten-free"
                    />
                  </div>
                </div>

                {/* Education */}
                <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mt-4">Education</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Degree</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editedPerson.education_degree || ''} onChange={(e) => setEditedPerson({...editedPerson, education_degree: e.target.value})}>
                      <option value="">Select...</option>
                      <option value="High School">High School</option>
                      <option value="Associate">Associate's</option>
                      <option value="Bachelor">Bachelor's</option>
                      <option value="Master">Master's</option>
                      <option value="Doctorate">Doctorate</option>
                      <option value="Professional">Professional (MD, JD, etc.)</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Institution</label>
                    <Input value={editedPerson.education_institution || ''} onChange={(e) => setEditedPerson({...editedPerson, education_institution: e.target.value})} placeholder="University / College name" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Graduation Year</label>
                    <Input value={editedPerson.education_graduation_year || ''} onChange={(e) => setEditedPerson({...editedPerson, education_graduation_year: e.target.value})} placeholder="e.g., 2018" />
                  </div>
                </div>

                {/* Social Media */}
                <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mt-4">Social Media</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Facebook</label>
                    <Input value={editedPerson.facebook_url || ''} onChange={(e) => setEditedPerson({...editedPerson, facebook_url: e.target.value})} placeholder="https://facebook.com/..." />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Twitter / X</label>
                    <Input value={editedPerson.twitter_url || ''} onChange={(e) => setEditedPerson({...editedPerson, twitter_url: e.target.value})} placeholder="https://x.com/..." />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Instagram</label>
                    <Input value={editedPerson.instagram_url || ''} onChange={(e) => setEditedPerson({...editedPerson, instagram_url: e.target.value})} placeholder="https://instagram.com/..." />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Website</label>
                    <Input value={editedPerson.website_url || ''} onChange={(e) => setEditedPerson({...editedPerson, website_url: e.target.value})} placeholder="https://..." />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Preferred Name</p>
                      <p className="text-sm font-medium">{person.preferred_name || '—'}</p>
                    </div>
                    {isAdmin && (
                      <div>
                        <p className="text-xs text-muted-foreground">Also known as</p>
                        <p className="text-sm font-medium" data-testid="personnel-name-aliases-display">{(person.name_aliases || []).join(', ') || '—'}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground">Date of Birth</p>
                      <p className="text-sm font-medium">{person.date_of_birth || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Gender</p>
                      <p className="text-sm font-medium">{person.gender || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Marital Status</p>
                      <p className="text-sm font-medium">{person.marital_status || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">SSN</p>
                      {(person.ssn_last_four || person.ssn_full) ? (
                        <div className="flex items-center gap-2">
                          {ssnRevealed ? (
                            <>
                              <p className="text-sm font-medium font-mono">{ssnFull || person.ssn_full || `***-**-${person.ssn_last_four}`}</p>
                              <button onClick={() => setSsnRevealed(false)} className="text-muted-foreground hover:text-foreground" data-testid="ssn-hide-btn"><EyeOff size={14} /></button>
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-medium font-mono">***-**-{person.ssn_last_four || '****'}</p>
                              <button onClick={revealSSN} disabled={ssnLoading} className="text-muted-foreground hover:text-foreground" data-testid="ssn-reveal-btn"><Eye size={14} /></button>
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm font-medium">—</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Personal Email</p>
                        {person.email ? (
                          <a href={`mailto:${person.email}`} className="text-sm text-primary hover:underline">{person.email}</a>
                        ) : (
                          <p className="text-sm font-medium">—</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Work Email</p>
                        {person.work_email ? (
                          <a href={`mailto:${person.work_email}`} className="text-sm text-primary hover:underline">{person.work_email}</a>
                        ) : (
                          <p className="text-sm font-medium">—</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        {person.phone ? (
                          <a href={`tel:${person.phone}`} className="text-sm text-primary hover:underline">{person.phone}</a>
                        ) : (
                          <p className="text-sm font-medium">—</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Mobile</p>
                        {person.mobile_phone ? (
                          <a href={`tel:${person.mobile_phone}`} className="text-sm text-primary hover:underline">{person.mobile_phone}</a>
                        ) : (
                          <p className="text-sm font-medium">—</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-3">
                    <MapPin size={16} className="text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground">Address</p>
                      <p className="text-sm">
                        {[person.address, person.city, person.state, person.zip_code].filter(Boolean).join(', ') || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mb-3">Additional Information</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <Linkedin size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">LinkedIn</p>
                        {person.linkedin_url ? (
                          <a href={person.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">View Profile</a>
                        ) : (
                          <p className="text-sm">—</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shirt size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Shirt Size</p>
                        <p className="text-sm">{person.shirt_size || '—'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Dietary Restrictions</p>
                      <p className="text-sm">{person.dietary_restrictions || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Education */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mb-3">Education</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div><p className="text-xs text-muted-foreground">Degree</p><p className="text-sm font-medium">{person.education_degree || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Institution</p><p className="text-sm font-medium">{person.education_institution || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Graduation Year</p><p className="text-sm font-medium">{person.education_graduation_year || '—'}</p></div>
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mb-3">Social Media</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <Facebook size={14} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Facebook</p>
                        {person.facebook_url ? (
                          <a href={person.facebook_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">View</a>
                        ) : (<p className="text-sm">—</p>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Twitter / X</p>
                        {person.twitter_url ? (
                          <a href={person.twitter_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">View</a>
                        ) : (<p className="text-sm">—</p>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Instagram size={14} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Instagram</p>
                        {person.instagram_url ? (
                          <a href={person.instagram_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">View</a>
                        ) : (<p className="text-sm">—</p>)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Website</p>
                        {person.website_url ? (
                          <a href={person.website_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">View</a>
                        ) : (<p className="text-sm">—</p>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
          
          {/* Custom Fields for Personal Tab */}
          {renderCustomFields('personal')}
        </TabsContent>

        {/* Job Tab */}
        <TabsContent value="job">
          {isAdmin && person?.is_interviewer && person?.id && (
            <Card className="p-6 mb-4" data-testid="personnel-booking-card">
              <h3 className="font-medium mb-4 flex items-center gap-2"><Calendar size={16} /> Booking link & Google Calendar</h3>
              <BookingLinkPanel personnelId={person.id} />
            </Card>
          )}
          {/* Employment Details Card */}
          <Card className="p-6" data-testid="job-employment-details">
            <h3 className="font-medium mb-4 flex items-center gap-2"><Briefcase size={16} /> Employment Details</h3>
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Division</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editedPerson.division} onChange={(e) => setEditedPerson({ ...editedPerson, division: e.target.value })}>
                      {divisions.map((div) => (<option key={div} value={div}>{div}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Type</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editedPerson.personnel_type} onChange={(e) => setEditedPerson({ ...editedPerson, personnel_type: e.target.value })}>
                      {types.map((type) => (<option key={type} value={type}>{type}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Discipline</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editedPerson.discipline || ''} onChange={(e) => setEditedPerson({ ...editedPerson, discipline: e.target.value })}>
                      <option value="">Select...</option>
                      {positionTypes.map((type) => (<option key={type} value={type}>{type}</option>))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editedPerson.status} onChange={(e) => setEditedPerson({ ...editedPerson, status: e.target.value })}>
                      {statuses.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Employment Status</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="employment-status-select" value={editedPerson.employment_status || ''} onChange={(e) => setEditedPerson({ ...editedPerson, employment_status: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="Full-Time">Full-Time</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="PRN">PRN</option>
                      <option value="Temporary">Temporary</option>
                      <option value="Seasonal">Seasonal</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Worker Type</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="worker-type-select" value={editedPerson.worker_type || ''} onChange={(e) => setEditedPerson({ ...editedPerson, worker_type: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="W2">W2 Employee</option>
                      <option value="1099">1099 Contractor</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Job Title</label>
                    <Input value={editedPerson.job_title || ''} onChange={(e) => setEditedPerson({ ...editedPerson, job_title: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Department</label>
                    <Input value={editedPerson.department || ''} onChange={(e) => setEditedPerson({ ...editedPerson, department: e.target.value })} placeholder="e.g., Physical Therapy" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Hire Date</label>
                    <Input type="date" value={editedPerson.hire_date || ''} onChange={(e) => setEditedPerson({ ...editedPerson, hire_date: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Original Hire Date</label>
                    <Input type="date" value={editedPerson.original_hire_date || ''} onChange={(e) => setEditedPerson({ ...editedPerson, original_hire_date: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Ideal Visits/Week</label>
                    <Input type="number" min="0" data-testid="ideal-visits-input" value={editedPerson.ideal_visits || ''} onChange={(e) => setEditedPerson({ ...editedPerson, ideal_visits: e.target.value ? parseInt(e.target.value) : null })} placeholder="Enter ideal visits" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Reports To</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="reports-to-select" value={editedPerson.reports_to || ''} onChange={(e) => setEditedPerson({ ...editedPerson, reports_to: e.target.value })}>
                      <option value="">None</option>
                      {allPersonnel.map((p) => (<option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.job_title ? ` (${p.job_title})` : ''}</option>))}
                    </select>
                  </div>
                </div>

                {/* EEO / Demographics */}
                <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mt-4">EEO / Demographics</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Ethnicity</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="ethnicity-select" value={editedPerson.ethnicity || ''} onChange={(e) => setEditedPerson({ ...editedPerson, ethnicity: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="Hispanic or Latino">Hispanic or Latino</option>
                      <option value="White (Not Hispanic)">White (Not Hispanic)</option>
                      <option value="Black or African American">Black or African American</option>
                      <option value="Asian">Asian</option>
                      <option value="American Indian or Alaska Native">American Indian or Alaska Native</option>
                      <option value="Native Hawaiian or Pacific Islander">Native Hawaiian or Pacific Islander</option>
                      <option value="Two or More Races">Two or More Races</option>
                      <option value="Prefer Not to Say">Prefer Not to Say</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">EEO Job Category</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="eeo-category-select" value={editedPerson.eeo_category || ''} onChange={(e) => setEditedPerson({ ...editedPerson, eeo_category: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="1.1 - Executive/Senior Officials">1.1 - Executive/Senior Officials</option>
                      <option value="1.2 - First/Mid Officials & Managers">1.2 - First/Mid Officials & Managers</option>
                      <option value="2 - Professionals">2 - Professionals</option>
                      <option value="3 - Technicians">3 - Technicians</option>
                      <option value="4 - Sales Workers">4 - Sales Workers</option>
                      <option value="5 - Administrative Support">5 - Administrative Support</option>
                      <option value="6 - Craft Workers">6 - Craft Workers</option>
                      <option value="7 - Operatives">7 - Operatives</option>
                      <option value="8 - Laborers & Helpers">8 - Laborers & Helpers</option>
                      <option value="9 - Service Workers">9 - Service Workers</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Veteran Status</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="veteran-status-select" value={editedPerson.veteran_status || ''} onChange={(e) => setEditedPerson({ ...editedPerson, veteran_status: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="Not a Veteran">Not a Veteran</option>
                      <option value="Active Duty">Active Duty</option>
                      <option value="Veteran">Veteran</option>
                      <option value="Disabled Veteran">Disabled Veteran</option>
                      <option value="Prefer Not to Say">Prefer Not to Say</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><p className="text-xs text-muted-foreground">Division</p><p className="text-sm font-medium">{person.division}</p></div>
                  <div><p className="text-xs text-muted-foreground">Type</p><p className="text-sm font-medium">{person.personnel_type}</p></div>
                  <div><p className="text-xs text-muted-foreground">Discipline</p><p className="text-sm font-medium">{person.discipline || '-'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Job Title</p><p className="text-sm font-medium">{person.job_title || '-'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Department</p><p className="text-sm font-medium">{person.department || '-'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Hire Date</p><p className="text-sm font-medium">{formatDate(person.hire_date) || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Original Hire Date</p><p className="text-sm font-medium">{formatDate(person.original_hire_date) || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Status</p><p className="text-sm font-medium">{person.status}</p></div>
                  <div><p className="text-xs text-muted-foreground">Employment Status</p><p className="text-sm font-medium">{person.employment_status || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Worker Type</p><p className="text-sm font-medium">{person.worker_type || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Ideal Visits/Week</p><p className="text-sm font-medium">{person.ideal_visits || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Reports To</p><p className="text-sm font-medium">{person.reports_to ? (allPersonnel.find(p => p.id === person.reports_to) ? `${allPersonnel.find(p => p.id === person.reports_to).first_name} ${allPersonnel.find(p => p.id === person.reports_to).last_name}` : person.reports_to) : '—'}</p></div>
                </div>
                {/* EEO Section */}
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-muted-foreground border-b pb-2 mb-3">EEO / Demographics</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div><p className="text-xs text-muted-foreground">Ethnicity</p><p className="text-sm font-medium">{person.ethnicity || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">EEO Job Category</p><p className="text-sm font-medium">{person.eeo_category || '—'}</p></div>
                    <div><p className="text-xs text-muted-foreground">Veteran Status</p><p className="text-sm font-medium">{person.veteran_status || '—'}</p></div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Direct Reports Card */}
          {directReports.length > 0 && (
            <Card className="p-6 mt-4" data-testid="job-direct-reports">
              <h3 className="font-medium mb-4 flex items-center gap-2"><UserPlus size={16} /> Direct Reports ({directReports.length})</h3>
              <div className="space-y-2">
                {directReports.map((dr) => (
                  <div key={dr.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate(`/personnel/${dr.id}`)} data-testid={`direct-report-${dr.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User size={14} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{dr.first_name} {dr.last_name}</p>
                        <p className="text-xs text-muted-foreground">{dr.job_title || dr.discipline || '-'}</p>
                      </div>
                    </div>
                    <Badge className={dr.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{dr.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Compensation History Card */}
          <Card className="p-6 mt-4" data-testid="job-compensation-history">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium flex items-center gap-2"><DollarSign size={16} /> Compensation History</h3>
              <Button variant="outline" size="sm" onClick={() => { setShowCompForm(!showCompForm); setCompForm({ rate: '', frequency: 'per_visit', start_date: '', notes: '', type: 'compensation' }); }} data-testid="add-compensation-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {showCompForm && compForm.type === 'compensation' && (
              <div className="p-4 border rounded-lg mb-4 bg-muted/20" data-testid="compensation-form">
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="text-xs font-medium text-muted-foreground">Rate ($)</label><Input type="number" step="0.01" value={compForm.rate} onChange={(e) => setCompForm({ ...compForm, rate: e.target.value })} placeholder="50.00" /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Frequency</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={compForm.frequency} onChange={(e) => setCompForm({ ...compForm, frequency: e.target.value })}>
                      <option value="per_visit">Per Visit</option><option value="hourly">Hourly</option><option value="salary">Salary (Annual)</option><option value="per_check">Per Check</option>
                    </select>
                  </div>
                  <div><label className="text-xs font-medium text-muted-foreground">Start Date</label><Input type="date" value={compForm.start_date} onChange={(e) => setCompForm({ ...compForm, start_date: e.target.value })} /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Notes</label><Input value={compForm.notes || ''} onChange={(e) => setCompForm({ ...compForm, notes: e.target.value })} placeholder="Reason" /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={addCompensation} data-testid="save-compensation-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCompForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
            {compensationHistory.filter(c => c.type === 'compensation' || !c.type).length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Rate</th><th className="text-left p-3 font-medium">Frequency</th><th className="text-left p-3 font-medium">Start</th><th className="text-left p-3 font-medium">End</th><th className="text-left p-3 font-medium">Notes</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {compensationHistory.filter(c => c.type === 'compensation' || !c.type).map((entry, i) => (
                      <tr key={entry.id} className={`border-t ${i === 0 ? 'bg-primary/5' : ''}`} data-testid={`comp-row-${entry.id}`}>
                        <td className="p-3 font-medium">${parseFloat(entry.rate).toFixed(2)}</td>
                        <td className="p-3 capitalize">{(entry.frequency || '').replace('_', ' ')}</td>
                        <td className="p-3">{formatDate(entry.start_date)}</td>
                        <td className="p-3">{entry.end_date ? formatDate(entry.end_date) : <Badge className="bg-green-100 text-green-800 text-xs">Current</Badge>}</td>
                        <td className="p-3 text-muted-foreground">{entry.notes || '-'}</td>
                        <td className="p-3"><button onClick={() => deleteCompensation(entry.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No compensation history recorded</p>
            )}
          </Card>

          {/* Bonus History Card */}
          <Card className="p-6 mt-4" data-testid="job-bonus-history">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium flex items-center gap-2"><Star size={16} /> Bonus History</h3>
              <Button variant="outline" size="sm" onClick={() => { setShowCompForm(true); setCompForm({ rate: '', frequency: 'one_time', start_date: '', notes: '', type: 'bonus' }); }} data-testid="add-bonus-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {showCompForm && compForm.type === 'bonus' && (
              <div className="p-4 border rounded-lg mb-4 bg-muted/20" data-testid="bonus-form">
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-xs font-medium text-muted-foreground">Amount ($)</label><Input type="number" step="0.01" value={compForm.rate} onChange={(e) => setCompForm({ ...compForm, rate: e.target.value })} placeholder="500.00" /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Date</label><Input type="date" value={compForm.start_date} onChange={(e) => setCompForm({ ...compForm, start_date: e.target.value })} /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Reason</label><Input value={compForm.notes || ''} onChange={(e) => setCompForm({ ...compForm, notes: e.target.value })} placeholder="Performance, Holiday..." /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={addCompensation} data-testid="save-bonus-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCompForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
            {compensationHistory.filter(c => c.type === 'bonus').length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Amount</th><th className="text-left p-3 font-medium">Date</th><th className="text-left p-3 font-medium">Reason</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {compensationHistory.filter(c => c.type === 'bonus').map((entry) => (
                      <tr key={entry.id} className="border-t" data-testid={`bonus-row-${entry.id}`}>
                        <td className="p-3 font-medium">${parseFloat(entry.rate).toFixed(2)}</td>
                        <td className="p-3">{formatDate(entry.start_date)}</td>
                        <td className="p-3 text-muted-foreground">{entry.notes || '-'}</td>
                        <td className="p-3"><button onClick={() => deleteCompensation(entry.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No bonus history recorded</p>
            )}
          </Card>

          {/* Commission History Card */}
          <Card className="p-6 mt-4" data-testid="job-commission-history">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium flex items-center gap-2"><TrendingUp size={16} /> Commission History</h3>
              <Button variant="outline" size="sm" onClick={() => { setShowCompForm(true); setCompForm({ rate: '', frequency: 'per_visit', start_date: '', notes: '', type: 'commission' }); }} data-testid="add-commission-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {showCompForm && compForm.type === 'commission' && (
              <div className="p-4 border rounded-lg mb-4 bg-muted/20" data-testid="commission-form">
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="text-xs font-medium text-muted-foreground">Rate ($)</label><Input type="number" step="0.01" value={compForm.rate} onChange={(e) => setCompForm({ ...compForm, rate: e.target.value })} placeholder="25.00" /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Frequency</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={compForm.frequency} onChange={(e) => setCompForm({ ...compForm, frequency: e.target.value })}>
                      <option value="per_visit">Per Visit</option><option value="per_referral">Per Referral</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  <div><label className="text-xs font-medium text-muted-foreground">Start Date</label><Input type="date" value={compForm.start_date} onChange={(e) => setCompForm({ ...compForm, start_date: e.target.value })} /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Notes</label><Input value={compForm.notes || ''} onChange={(e) => setCompForm({ ...compForm, notes: e.target.value })} placeholder="Details" /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={addCompensation} data-testid="save-commission-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCompForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
            {compensationHistory.filter(c => c.type === 'commission').length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Rate</th><th className="text-left p-3 font-medium">Frequency</th><th className="text-left p-3 font-medium">Start</th><th className="text-left p-3 font-medium">End</th><th className="text-left p-3 font-medium">Notes</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {compensationHistory.filter(c => c.type === 'commission').map((entry) => (
                      <tr key={entry.id} className="border-t" data-testid={`commission-row-${entry.id}`}>
                        <td className="p-3 font-medium">${parseFloat(entry.rate).toFixed(2)}</td>
                        <td className="p-3 capitalize">{(entry.frequency || '').replace('_', ' ')}</td>
                        <td className="p-3">{formatDate(entry.start_date)}</td>
                        <td className="p-3">{entry.end_date ? formatDate(entry.end_date) : <Badge className="bg-green-100 text-green-800 text-xs">Current</Badge>}</td>
                        <td className="p-3 text-muted-foreground">{entry.notes || '-'}</td>
                        <td className="p-3"><button onClick={() => deleteCompensation(entry.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No commission history recorded</p>
            )}
          </Card>

          {/* Job Role History Card */}
          <Card className="p-6 mt-4" data-testid="job-role-history">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium flex items-center gap-2"><History size={16} /> Job Role History</h3>
              <Button variant="outline" size="sm" onClick={() => { setShowJobHistoryForm(!showJobHistoryForm); setJobHistoryForm({ title: '', department: '', start_date: '', notes: '' }); }} data-testid="add-job-history-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {showJobHistoryForm && (
              <div className="p-4 border rounded-lg mb-4 bg-muted/20" data-testid="job-history-form">
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="text-xs font-medium text-muted-foreground">Title</label><Input value={jobHistoryForm.title} onChange={(e) => setJobHistoryForm({ ...jobHistoryForm, title: e.target.value })} placeholder="Physical Therapist" /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Department</label><Input value={jobHistoryForm.department || ''} onChange={(e) => setJobHistoryForm({ ...jobHistoryForm, department: e.target.value })} placeholder="Therapy" /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Start Date</label><Input type="date" value={jobHistoryForm.start_date} onChange={(e) => setJobHistoryForm({ ...jobHistoryForm, start_date: e.target.value })} /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Notes</label><Input value={jobHistoryForm.notes || ''} onChange={(e) => setJobHistoryForm({ ...jobHistoryForm, notes: e.target.value })} placeholder="Promotion, transfer..." /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={addJobHistoryEntry} data-testid="save-job-history-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowJobHistoryForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
            {jobHistory.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Title</th><th className="text-left p-3 font-medium">Department</th><th className="text-left p-3 font-medium">Start</th><th className="text-left p-3 font-medium">End</th><th className="text-left p-3 font-medium">Notes</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {jobHistory.map((entry, i) => (
                      <tr key={entry.id} className={`border-t ${i === 0 ? 'bg-primary/5' : ''}`} data-testid={`job-history-row-${entry.id}`}>
                        <td className="p-3 font-medium">{entry.title}</td>
                        <td className="p-3">{entry.department || '-'}</td>
                        <td className="p-3">{formatDate(entry.start_date)}</td>
                        <td className="p-3">{entry.end_date ? formatDate(entry.end_date) : <Badge className="bg-green-100 text-green-800 text-xs">Current</Badge>}</td>
                        <td className="p-3 text-muted-foreground">{entry.notes || '-'}</td>
                        <td className="p-3"><button onClick={() => deleteJobHistory(entry.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No job role history recorded</p>
            )}
          </Card>

          {/* Custom Fields for Job Tab */}
          {renderCustomFields('job')}
        </TabsContent>

        {/* Stats Tab - Only for personnel with therapist_profile_id (therapists) */}
        {person?.therapist_profile_id && (
          <TabsContent value="dashboard">
            <div className="space-y-6">
              {/* Visit Statistics */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-medium text-lg">Visit Statistics</h3>
                    <p className="text-sm text-muted-foreground">
                      Performance metrics based on visit data
                    </p>
                  </div>
                  {visitsLoading && (
                    <span className="text-sm text-muted-foreground">Loading...</span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* YTD Visits */}
                  <Card className="p-4 border" data-testid="stat-ytd-visits">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          YTD Visits
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-2 tabular-nums">
                          {visitStats.ytd_visits}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-green-100">
                        <Calendar className="text-green-600" size={24} strokeWidth={1.5} />
                      </div>
                    </div>
                  </Card>

                  {/* Avg Visits/Week */}
                  <Card className="p-4 border" data-testid="stat-avg-visits">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Avg Visits/Week
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-2 tabular-nums">
                          {visitStats.avg_visits_per_week.toFixed(1)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-blue-100">
                        <TrendingUp className="text-blue-600" size={24} strokeWidth={1.5} />
                      </div>
                    </div>
                  </Card>

                  {/* Avg Missed (6wks) */}
                  <Card className="p-4 border" data-testid="stat-avg-missed">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Avg Missed (6wks)
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-2 tabular-nums">
                          {visitStats.avg_missed_last_6_weeks.toFixed(1)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-orange-100">
                        <AlertCircle className="text-orange-600" size={24} strokeWidth={1.5} />
                      </div>
                    </div>
                  </Card>

                  {/* Missed Last Upload */}
                  <Card className="p-4 border" data-testid="stat-missed-last">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Missed Last Upload
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-2 tabular-nums">
                          {visitStats.missed_last_upload}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-100">
                        <XCircle className="text-red-600" size={24} strokeWidth={1.5} />
                      </div>
                    </div>
                  </Card>

                  {/* Avg Late Notes (6wks) */}
                  <Card className="p-4 border" data-testid="stat-avg-late-notes">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Avg Late Notes (6wks)
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-2 tabular-nums">
                          {visitStats.avg_late_notes_last_6_weeks.toFixed(1)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-100">
                        <Clock className="text-amber-600" size={24} strokeWidth={1.5} />
                      </div>
                    </div>
                  </Card>

                  {/* Late Notes Last Upload */}
                  <Card className="p-4 border" data-testid="stat-late-notes-last">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Late Notes Last Upload
                        </p>
                        <p className="text-3xl font-bold text-foreground mt-2 tabular-nums">
                          {visitStats.late_notes_last_upload}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-purple-100">
                        <Clock className="text-purple-600" size={24} strokeWidth={1.5} />
                      </div>
                    </div>
                  </Card>
                </div>
              </Card>

              {/* Cities Coverage */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-medium text-lg flex items-center gap-2">
                      <Building size={20} />
                      Cities Coverage
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Cities where this therapist has had visits or is willing to cover
                    </p>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {citiesCoverage.visit_cities_count} from visits • {citiesCoverage.manual_cities_count} manually added
                  </div>
                </div>

                {/* Add City Form */}
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Add a city (e.g., Houston, TX)"
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCity()}
                    className="flex-1"
                    data-testid="add-city-input"
                  />
                  <Button 
                    onClick={handleAddCity} 
                    disabled={addingCity || !newCity.trim()}
                    data-testid="add-city-btn"
                  >
                    <Plus size={16} className="mr-1" />
                    Add City
                  </Button>
                </div>

                {/* Cities List */}
                {citiesCoverage.cities.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No cities in coverage area yet. Cities will appear here as visits are recorded, or you can add them manually.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {citiesCoverage.cities.map((item, index) => (
                      <Badge
                        key={index}
                        variant={item.source === 'visits' ? 'secondary' : 'outline'}
                        className={`px-3 py-1.5 text-sm ${
                          item.source === 'visits' 
                            ? 'bg-blue-100 text-blue-800 border-blue-200' 
                            : 'bg-green-50 text-green-800 border-green-300'
                        }`}
                      >
                        <MapPin size={12} className="mr-1" />
                        {item.city}
                        {item.source === 'visits' && (
                          <span className="ml-1 text-xs opacity-70">(from visits)</span>
                        )}
                        {item.removable && (
                          <button
                            onClick={() => handleRemoveCity(item.city)}
                            className="ml-2 hover:text-red-600"
                            title="Remove city"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        )}

        {/* My Job Duties Tab */}
        <TabsContent value="duties">
          <Card className="p-6">
            <h3 className="font-medium mb-4">My Job Duties</h3>
            {jobDuties.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No job duties assigned
              </p>
            ) : (
              <div className="space-y-4">
                {jobDuties.map((duty) => (
                  <div
                    key={duty.id}
                    className="p-4 border rounded-lg hover:bg-muted/20"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{duty.title}</h4>
                        {duty.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {duty.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            duty.status === 'Active'
                              ? 'bg-green-100 text-green-800'
                              : duty.status === 'Completed'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }
                        >
                          {duty.status}
                        </Badge>
                        <Badge
                          className={
                            duty.priority === 'Urgent'
                              ? 'bg-red-100 text-red-800'
                              : duty.priority === 'High'
                              ? 'bg-orange-100 text-orange-800'
                              : duty.priority === 'Normal'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }
                        >
                          {duty.priority}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {duty.due_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          Due: {formatDate(duty.due_date)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Bell size={14} />
                        {duty.reminders?.filter((r) => !r.sent).length || 0} pending reminders
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText size={14} />
                        {duty.documents?.length || 0} documents
                      </span>
                    </div>
                    {duty.reminders && duty.reminders.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Reminders:
                        </p>
                        <div className="space-y-2">
                          {duty.reminders
                            .filter((r) => !r.completed)
                            .slice(0, 5)
                            .map((reminder) => {
                              const reminderDate = new Date(reminder.reminder_date);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const isOverdue = reminderDate < today;
                              const isToday = reminderDate.toDateString() === today.toDateString();
                              
                              return (
                                <div
                                  key={reminder.id}
                                  className={`flex items-center justify-between p-2 rounded ${
                                    isOverdue ? 'bg-red-50 border border-red-200' :
                                    isToday ? 'bg-yellow-50 border border-yellow-200' :
                                    'bg-muted/30'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 text-sm">
                                    <Bell size={14} className={isOverdue ? 'text-red-500' : isToday ? 'text-yellow-600' : 'text-orange-500'} />
                                    <span className="font-medium">
                                      {formatDate(reminder.reminder_date)} at {reminder.reminder_time}
                                    </span>
                                    {reminder.email_subject && (
                                      <span className="text-muted-foreground">
                                        - {reminder.email_subject}
                                      </span>
                                    )}
                                    {isOverdue && (
                                      <Badge className="bg-red-100 text-red-800 text-xs">Overdue</Badge>
                                    )}
                                    {isToday && !isOverdue && (
                                      <Badge className="bg-yellow-100 text-yellow-800 text-xs">Due Today</Badge>
                                    )}
                                    {reminder.is_recurring && (
                                      <Badge className="bg-purple-100 text-purple-800 text-xs">
                                        Recurring
                                      </Badge>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                                    onClick={() => handleCompleteReminder(duty.id, reminder.id)}
                                  >
                                    <CheckCircle size={12} className="mr-1" />
                                    Complete
                                  </Button>
                                </div>
                              );
                            })}
                          {duty.reminders.filter((r) => r.completed).length > 0 && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {duty.reminders.filter((r) => r.completed).length} completed reminder(s)
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
          {renderCustomFields('job')}
        </TabsContent>

        {/* Time Off Tab */}
        <TabsContent value="timeoff">
          <PersonnelPTO personnelId={personnelId} isAdmin={true} />
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <PersonnelPerformance 
            personnelId={personnelId} 
            personName={`${person.first_name} ${person.last_name}`}
            isAdmin={true} 
          />
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance">
          <PersonnelCompliance personnelId={personnelId} isAdmin={true} />
        </TabsContent>

        {/* Emergency Tab */}
        <TabsContent value="emergency">
          <Card className="p-6">
            <h3 className="font-medium mb-4">Emergency Contact</h3>
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <Input
                      value={editedPerson.emergency_contact?.name || ''}
                      onChange={(e) =>
                        setEditedPerson({
                          ...editedPerson,
                          emergency_contact: {
                            ...editedPerson.emergency_contact,
                            name: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Relationship</label>
                    <Input
                      value={editedPerson.emergency_contact?.relationship || ''}
                      onChange={(e) =>
                        setEditedPerson({
                          ...editedPerson,
                          emergency_contact: {
                            ...editedPerson.emergency_contact,
                            relationship: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <Input
                      value={editedPerson.emergency_contact?.phone || ''}
                      onChange={(e) =>
                        setEditedPerson({
                          ...editedPerson,
                          emergency_contact: {
                            ...editedPerson.emergency_contact,
                            phone: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <Input
                      type="email"
                      value={editedPerson.emergency_contact?.email || ''}
                      onChange={(e) =>
                        setEditedPerson({
                          ...editedPerson,
                          emergency_contact: {
                            ...editedPerson.emergency_contact,
                            email: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {person.emergency_contact?.name ? (
                  <>
                    <p><strong>Name:</strong> {person.emergency_contact.name}</p>
                    <p><strong>Relationship:</strong> {person.emergency_contact.relationship || '-'}</p>
                    <p><strong>Phone:</strong> {person.emergency_contact.phone || '-'}</p>
                    <p><strong>Email:</strong> {person.emergency_contact.email || '-'}</p>
                  </>
                ) : (
                  <p className="text-muted-foreground">No emergency contact on file</p>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card className="p-6 mb-4">
            <PersonnelRequiredCredentials personnelId={personnelId} />
          </Card>
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h4 className="font-medium text-sm text-foreground" data-testid="current-credentials-header">Current Documents</h4>
                <div className="flex flex-wrap items-center gap-2">
                  {(() => {
                    const all = getAllCredentials();
                    const current = filterByCategory(all.current);
                    const eligibleIds = current.filter(d => d._source === 'embedded' || d.id).map(d => d.id);
                    const selectedCount = selectedDocIds.size;
                    return (
                      <>
                        {selectedCount > 0 && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleBulkDownload(Array.from(selectedDocIds))}
                              data-testid="download-selected-btn"
                            >
                              <Download size={14} className="mr-1" /> Download Selected ({selectedCount})
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEmailAttachPrefill({
                                document_ids: Array.from(selectedDocIds),
                                label: `${selectedCount} credential${selectedCount !== 1 ? 's' : ''} from ${person?.first_name || ''} ${person?.last_name || ''}`.trim(),
                              })}
                              data-testid="email-selected-btn"
                            >
                              <Mail size={14} className="mr-1" /> Email Selected ({selectedCount})
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive border-destructive/40 hover:bg-destructive/10"
                              onClick={handleBulkDeleteDocuments}
                              data-testid="delete-selected-btn"
                            >
                              <Trash2 size={14} className="mr-1" /> Delete Selected ({selectedCount})
                            </Button>
                          </>
                        )}
                        {eligibleIds.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBulkDownload([])}
                            data-testid="download-all-btn"
                            title="Download all current documents in this category as a zip"
                          >
                            <Download size={14} className="mr-1" /> Download All ({eligibleIds.length})
                          </Button>
                        )}
                        {(() => {
                          const lostIds = current.filter(d => d._source === 'embedded' && d.file_available === false).map(d => d.id);
                          if (lostIds.length === 0) return null;
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-700 border-amber-300 hover:bg-amber-50"
                              data-testid="delete-lost-files-btn"
                              title="Remove records whose underlying file is no longer on the server"
                              onClick={async () => {
                                if (!window.confirm(`Remove ${lostIds.length} record${lostIds.length === 1 ? '' : 's'} whose underlying file is no longer on the server? After this they will show as MISSING in audits (so the system stops claiming the file exists). The DB record will be deleted; nothing was recoverable anyway.`)) return;
                                try {
                                  const res = await axios.post(`${API}/documents/bulk-delete`, { document_ids: lostIds });
                                  toast.success(`Removed ${res.data.deleted} lost record${res.data.deleted === 1 ? '' : 's'}`);
                                  setSelectedDocIds(new Set());
                                  fetchPerson();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Failed to remove lost records');
                                }
                              }}
                            >
                              <Trash2 size={14} className="mr-1" /> Remove Lost ({lostIds.length})
                            </Button>
                          );
                        })()}
                      </>
                    );
                  })()}
                  <div className="flex gap-1 bg-muted rounded-lg p-1" data-testid="doc-category-tabs">
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'credential', label: 'Credentials' },
                      { id: 'tax', label: 'Tax Documents' },
                      { id: 'other', label: 'Other' },
                    ].map(t => (
                      <button
                        key={t.id}
                        onClick={() => { setDocCategory(t.id); setSelectedDocIds(new Set()); }}
                        className={`text-xs px-3 py-1 rounded-md transition-colors ${docCategory === t.id ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid={`doc-cat-${t.id}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Column Headers */}
              <div className="grid grid-cols-[28px_1fr_1fr_1fr_1fr] gap-4 items-center">
                <div>
                  {(() => {
                    const all = getAllCredentials();
                    const current = filterByCategory(all.current);
                    const eligibleIds = current.map(d => d.id).filter(Boolean);
                    const allSelected = eligibleIds.length > 0 && eligibleIds.every(id => selectedDocIds.has(id));
                    return (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedDocIds(new Set(eligibleIds));
                          else setSelectedDocIds(new Set());
                        }}
                        className="h-4 w-4 rounded border-gray-300"
                        data-testid="select-all-docs-checkbox"
                        title="Select all"
                      />
                    );
                  })()}
                </div>
                <div><label className="text-sm font-medium text-muted-foreground">Document Name</label></div>
                <div><label className="text-sm font-medium text-muted-foreground">Type</label></div>
                <div><label className="text-sm font-medium text-muted-foreground">{docCategory === 'tax' ? 'Tax Year' : 'Expiration'}</label></div>
                <div><label className="text-sm font-medium text-muted-foreground">Actions</label></div>
              </div>

              {/* Outstanding e-sign requests (e.g. TB Questionnaire, Skills Checklist) */}
              {credentialRequests.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2" data-testid="credential-requests-banner">
                  <span className="text-xs font-medium text-blue-800">Awaiting signature:</span>
                  {credentialRequests.map((r, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-0.5 rounded-full border border-blue-300 bg-white text-blue-700"
                      title={`${r.template_name || r.credential_type} — sent${r.requested_at ? ' ' + new Date(r.requested_at).toLocaleDateString() : ''}`}
                      data-testid={`credential-requested-${r.credential_type}`}
                    >
                      {r.credential_type} · Requested
                    </span>
                  ))}
                </div>
              )}

              {/* Credential rows - unified from all sources, deduped by type */}
              {(() => {
                const all = getAllCredentials();
                const current = filterByCategory(all.current);
                const archived = filterByCategory(all.archived);
                return (
                  <>
                    {current.map((doc) => {
                      const credStatus = docCategory !== 'tax' ? getCredentialStatus(doc) : null;
                      const isTax = (doc.category || getCategoryForType(doc.document_type)) === 'tax';
                      return (
                      <div key={doc.id || doc._id} className={`grid grid-cols-[28px_1fr_1fr_1fr_1fr] gap-4 items-center py-2 border-b ${credStatus?.level === 'expired' ? 'border-red-200 bg-red-50/50' : credStatus?.level === 'warning' ? 'border-yellow-200 bg-yellow-50/50' : 'border-muted/40'}`} data-testid={`credential-row-${doc.id}`}>
                        <div>
                          <input
                            type="checkbox"
                            checked={selectedDocIds.has(doc.id)}
                            onChange={() => toggleDocSelection(doc.id)}
                            className="h-4 w-4 rounded border-gray-300"
                            data-testid={`select-doc-${doc.id}`}
                          />
                        </div>
                        <div className="text-sm flex items-center gap-1.5">
                          {credStatus && (
                            <AlertCircle size={14} className={credStatus.level === 'expired' ? 'text-red-500 shrink-0' : 'text-yellow-500 shrink-0'} />
                          )}
                          {doc.document_name || doc.document_type}
                          {isTax && (
                            <Badge className="ml-1 text-[10px] bg-purple-100 text-purple-700">Tax</Badge>
                          )}
                          {doc.file_available === false && (
                            <Badge
                              className="ml-1 text-[10px] bg-red-100 text-red-700 border-red-200"
                              title="The original file is gone from the server. Please re-upload."
                              data-testid={`missing-file-badge-${doc.id}`}
                            >
                              File missing
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{doc.document_type}</div>
                        <div className={`text-sm ${credStatus?.level === 'expired' ? 'text-red-600 font-medium' : credStatus?.level === 'warning' ? 'text-yellow-600 font-medium' : 'text-muted-foreground'}`}>
                          {isTax
                            ? (doc.tax_year || '—')
                            : (doc.expiration_date ? formatDate(doc.expiration_date) : '—')}
                          {!isTax && credStatus && <span className="text-xs ml-1">({credStatus.label})</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingDocId(doc.id)}
                            title="Edit credential info"
                            data-testid={`edit-doc-${doc.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreviewDocument(doc)}
                            title={doc.file_available === false ? 'File missing — cannot preview' : 'Preview'}
                            disabled={doc.file_available === false}
                            data-testid={`preview-btn-${doc.id}`}
                          >
                            <Eye size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => doc._source === 'embedded' ? handleDownloadDocument(doc.id, doc.file_name, doc) : handleDownloadTherapistDocument(doc)}
                            title={doc.file_available === false ? 'File missing — cannot download' : 'Download'}
                            disabled={doc.file_available === false}
                          >
                            <Download size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendCredentialToAgencies(doc)}
                            title="Send this credential to the therapist's enabled agencies"
                            disabled={doc.file_available === false || sendingDocId === doc.id}
                            data-testid={`send-to-agencies-${doc.id}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {sendingDocId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          </Button>
                          {['PT License','PTA License','OT License','COTA License','ST License'].includes(doc.document_type) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startLicenseVerify(doc)}
                              title="Verify this license with the state board (TX PT/PTA)"
                              data-testid={`verify-license-${doc.id}`}
                              className="text-emerald-600 hover:text-emerald-800"
                            >
                              <ShieldCheck size={14} />
                            </Button>
                          )}
                          {!isTax && credStatus && (credStatus.level === 'expired' || credStatus.level === 'warning') && (person?.email || person?.work_email) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const prefill = buildCredentialReminder({
                                  recipientName: `${person.first_name || ''} ${person.last_name || ''}`.trim(),
                                  recipientEmail: person.email || person.work_email,
                                  credentials: [{
                                    type: doc.document_type || doc.document_name || 'Credential',
                                    status: credStatus.level === 'expired' ? 'expired' : 'expiring',
                                    expiration_date: doc.expiration_date,
                                  }],
                                });
                                setComposePrefill(prefill);
                              }}
                              title={`Email reminder to ${person.first_name || ''}`}
                              data-testid={`remind-doc-${doc.id}`}
                              className="text-amber-600 hover:text-amber-800"
                            >
                              <Bell size={14} />
                            </Button>
                          )}
                          {doc._source === 'embedded' && (
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteDocument(doc.id)} title="Delete">
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                    })}

                    {current.length === 0 && (
                      <p className="text-center text-muted-foreground py-2 text-sm">
                        No {docCategory === 'all' ? 'documents' : docCategory === 'tax' ? 'tax documents' : docCategory + ' documents'} yet
                      </p>
                    )}

              {/* Upload new credential row */}
              <div className="pt-2 border-t border-muted/40">
                <DropZone
                  file={selectedFile}
                  onFile={(f) => {
                    setSelectedFile(f);
                    if (!documentName.trim()) {
                      const nameWithoutExt = f.name.replace(/\.[^/.]+$/, '');
                      setDocumentName(nameWithoutExt);
                    }
                  }}
                  onClear={() => setSelectedFile(null)}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.bmp,.webp,.txt,.csv,.xlsx,.xls"
                  label="Drag and drop a document"
                  hint="PDF, image, DOC, or spreadsheet"
                  disabled={uploadingDoc}
                  testId="personnel-doc-upload"
                  compact
                  className="mb-3"
                />
              </div>
              <div className="grid grid-cols-[28px_1fr_1fr_1fr_1fr] gap-4 items-end">
                <div></div>
                <div>
                  <Input
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    placeholder="e.g., PT License"
                  />
                  {selectedFile && (
                    <p className="text-xs text-muted-foreground mt-1 truncate" title={selectedFile.name}>
                      File: {selectedFile.name}
                    </p>
                  )}
                </div>
                <div>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={documentType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setDocumentType(newType);
                      // Auto-fill document name if empty
                      if (!documentName.trim()) setDocumentName(newType);
                    }}
                  >
                    <optgroup label="Credentials">
                      <option value="License">License</option>
                      <option value="Certification">Certification</option>
                      <option value="CPR">CPR Card</option>
                      <option value="W9">W-9</option>
                      <option value="Contract">Contract</option>
                      <option value="ID">ID/Driver's License</option>
                      <option value="Insurance">Insurance</option>
                      <option value="Background Check">Background Check</option>
                    </optgroup>
                    <optgroup label="Tax Documents">
                      <option value="W2">W2</option>
                      <option value="1099">1099</option>
                    </optgroup>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  {getCategoryForType(documentType) === 'tax' ? (
                    <Input
                      type="number"
                      placeholder="Tax Year (e.g. 2024)"
                      value={taxYear}
                      onChange={(e) => setTaxYear(e.target.value)}
                      data-testid="tax-year-input"
                    />
                  ) : (
                    <Input
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                    />
                  )}
                </div>
                <div className="flex items-end gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {!selectedFile ? (
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingDoc}
                    >
                      <Upload size={16} className="mr-2" />
                      Select File
                    </Button>
                  ) : (
                    <Button
                      onClick={handleFileUpload}
                      disabled={uploadingDoc || !documentName.trim()}
                    >
                      <Upload size={16} className="mr-2" />
                      {uploadingDoc ? 'Uploading...' : 'Upload'}
                    </Button>
                  )}
                </div>
              </div>

              {/* Archived Section - uses unified deduplication */}
                    {archived.length > 0 && (
                      <div className="border-t pt-4 space-y-2">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2" data-testid="archived-section-header">
                            <Archive size={16} />
                            Archived ({archived.length})
                          </h4>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive/40 hover:bg-destructive/10"
                            onClick={async () => {
                              const ids = archived.filter(d => d._source === 'embedded').map(d => d.id);
                              if (ids.length === 0) {
                                toast.error('No archived documents to delete');
                                return;
                              }
                              if (!window.confirm(`Permanently delete all ${ids.length} archived document${ids.length === 1 ? '' : 's'} for this person? This cannot be undone.`)) return;
                              try {
                                const res = await axios.post(`${API}/documents/bulk-delete`, { document_ids: ids });
                                toast.success(`Deleted ${res.data.deleted} archived document${res.data.deleted === 1 ? '' : 's'}`);
                                fetchPerson();
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to delete archived documents');
                              }
                            }}
                            data-testid="delete-all-archived-btn"
                          >
                            <Trash2 size={14} className="mr-1" /> Delete All Archived
                          </Button>
                        </div>
                        <div className="bg-muted/20 rounded-lg p-3 space-y-2">
                          {archived.map((doc) => (
                            <div
                              key={`arch-${doc.id || doc._id}`}
                              className="flex items-center justify-between p-2 bg-background rounded border border-dashed"
                            >
                              <div className="flex items-center gap-3">
                                <FileText size={16} className="text-muted-foreground" />
                                <div>
                                  <p className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                                    {doc.document_name || doc.document_type}
                                    {doc.file_available === false && (
                                      <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200">File missing</Badge>
                                    )}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {doc.document_type}
                                    {doc.archived_at && ` • Archived: ${formatDate(doc.archived_at)}`}
                                    {doc.expiration_date && ` • Expires: ${formatDate(doc.expiration_date)}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePreviewDocument(doc, true)}
                                  title={doc.file_available === false ? 'File missing — cannot preview' : 'Preview'}
                                  disabled={doc.file_available === false}
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => doc._source === 'embedded' ? handleRestoreArchivedDocument(doc.id) : handleRestoreTherapistCredential(doc.id)}
                                  title="Restore to current"
                                  className="text-primary hover:text-primary"
                                >
                                  <ArrowLeft size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => doc._source === 'embedded' ? handleDownloadArchivedDocument(doc.id, doc.file_name, doc) : handleDownloadTherapistDocument(doc)}
                                  title={doc.file_available === false ? 'File missing — cannot download' : 'Download'}
                                  disabled={doc.file_available === false}
                                >
                                  <Download size={14} />
                                </Button>
                                {doc._source === 'embedded' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={async () => {
                                      if (!window.confirm('Permanently delete this archived document? This cannot be undone.')) return;
                                      try {
                                        await axios.post(`${API}/documents/bulk-delete`, { document_ids: [doc.id] });
                                        toast.success('Archived document deleted');
                                        fetchPerson();
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || 'Failed to delete');
                                      }
                                    }}
                                    title="Delete permanently"
                                    data-testid={`delete-archived-${doc.id}`}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes">
          <Card className="p-6">
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <MentionTextarea
                    value={newNote}
                    onChange={setNewNote}
                    mentions={noteMentions}
                    onMentionsChange={setNoteMentions}
                    placeholder="Add a note… type @ to tag a teammate"
                    rows={3}
                    testId="personnel-note"
                    onSubmitShortcut={handleAddNote}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <select
                    className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={noteType}
                    onChange={(e) => setNoteType(e.target.value)}
                  >
                    <option value="General">General</option>
                    <option value="Performance">Performance</option>
                    <option value="HR">HR</option>
                    <option value="Training">Training</option>
                  </select>
                  <Button onClick={handleAddNote} disabled={addingNote}>
                    <Plus size={16} className="mr-2" />
                    {addingNote ? 'Adding...' : 'Add'}
                  </Button>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                {person.notes && person.notes.length > 0 ? (
                  person.notes
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((note) => (
                      <div key={note.id} className="p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">{note.note_type}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {resolveName(note.author)} • {formatDateTime(note.created_at)}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                            {note.mentions?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {note.mentions.map((m) => (
                                  <span key={m.user_id} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-full px-1.5 py-0.5">
                                    @{m.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))
                ) : (
                  <p className="text-center text-muted-foreground py-4">No notes</p>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Benefits Tab */}
        <TabsContent value="benefits">
          <Card className="p-6" data-testid="benefits-info-card">
            <h3 className="font-medium mb-4 flex items-center gap-2"><Heart size={16} /> Benefits Information</h3>
            {isEditing ? (
              <div className="space-y-4">
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editedPerson.benefits_eligible} onChange={(e) => setEditedPerson({ ...editedPerson, benefits_eligible: e.target.checked })} className="rounded" />
                    <span className="text-sm">Benefits Eligible</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editedPerson.benefits_enrolled} onChange={(e) => setEditedPerson({ ...editedPerson, benefits_enrolled: e.target.checked })} className="rounded" />
                    <span className="text-sm">Currently Enrolled</span>
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Benefits Group</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="benefits-group-select" value={editedPerson.benefits_group || ''} onChange={(e) => setEditedPerson({ ...editedPerson, benefits_group: e.target.value })}>
                      <option value="">None</option>
                      <option value="Standard">Standard</option>
                      <option value="Premium">Premium</option>
                      <option value="Basic">Basic</option>
                      <option value="Executive">Executive</option>
                      <option value="Part-Time">Part-Time</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Charge Amount ($)</label>
                    <Input type="number" step="0.01" data-testid="benefits-charge-amount" value={editedPerson.benefits_charge_amount || ''} onChange={(e) => setEditedPerson({ ...editedPerson, benefits_charge_amount: e.target.value })} placeholder="150.00" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Charge Frequency</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="benefits-charge-frequency" value={editedPerson.benefits_charge_frequency || ''} onChange={(e) => setEditedPerson({ ...editedPerson, benefits_charge_frequency: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="per_check">Per Check</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Notes</label>
                  <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editedPerson.benefits_notes || ''} onChange={(e) => setEditedPerson({ ...editedPerson, benefits_notes: e.target.value })} placeholder="Additional benefit details..." />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Eligible</p>
                    <p className="text-sm font-medium flex items-center gap-1">{person.benefits_eligible ? <><CheckCircle size={14} className="text-green-600" /> Yes</> : <><XCircle size={14} className="text-gray-400" /> No</>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Enrolled</p>
                    <p className="text-sm font-medium flex items-center gap-1">{person.benefits_enrolled ? <><CheckCircle size={14} className="text-green-600" /> Yes</> : <><XCircle size={14} className="text-gray-400" /> No</>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Benefits Group</p>
                    <p className="text-sm font-medium">{person.benefits_group || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Charge Amount</p>
                    <p className="text-sm font-medium">{person.benefits_charge_amount ? `$${parseFloat(person.benefits_charge_amount).toFixed(2)}` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Charge Frequency</p>
                    <p className="text-sm font-medium capitalize">{(person.benefits_charge_frequency || '-').replace('_', ' ')}</p>
                  </div>
                </div>
                {person.benefits_notes && (
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground">Notes</p>
                    <p className="text-sm">{person.benefits_notes}</p>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Charge History */}
          <Card className="p-6 mt-4" data-testid="benefits-charge-history">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium flex items-center gap-2"><DollarSign size={16} /> Charge History</h3>
              <Button variant="outline" size="sm" onClick={() => { setShowCompForm(true); setCompForm({ rate: '', frequency: 'per_check', start_date: '', notes: '', type: 'benefit_charge' }); }} data-testid="add-charge-btn">
                <Plus size={14} className="mr-1" /> Add Charge
              </Button>
            </div>
            {showCompForm && compForm.type === 'benefit_charge' && (
              <div className="p-4 border rounded-lg mb-4 bg-muted/20" data-testid="charge-form">
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="text-xs font-medium text-muted-foreground">Amount ($)</label><Input type="number" step="0.01" value={compForm.rate} onChange={(e) => setCompForm({ ...compForm, rate: e.target.value })} placeholder="150.00" /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Frequency</label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={compForm.frequency} onChange={(e) => setCompForm({ ...compForm, frequency: e.target.value })}>
                      <option value="per_check">Per Check</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annually">Annually</option>
                    </select>
                  </div>
                  <div><label className="text-xs font-medium text-muted-foreground">Start Date</label><Input type="date" value={compForm.start_date} onChange={(e) => setCompForm({ ...compForm, start_date: e.target.value })} /></div>
                  <div><label className="text-xs font-medium text-muted-foreground">Notes</label><Input value={compForm.notes || ''} onChange={(e) => setCompForm({ ...compForm, notes: e.target.value })} placeholder="Plan change, etc." /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={addCompensation} data-testid="save-charge-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCompForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
            {compensationHistory.filter(c => c.type === 'benefit_charge').length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr><th className="text-left p-3 font-medium">Amount</th><th className="text-left p-3 font-medium">Frequency</th><th className="text-left p-3 font-medium">Start</th><th className="text-left p-3 font-medium">End</th><th className="text-left p-3 font-medium">Notes</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {compensationHistory.filter(c => c.type === 'benefit_charge').map((entry, i) => (
                      <tr key={entry.id} className={`border-t ${i === 0 ? 'bg-primary/5' : ''}`} data-testid={`charge-row-${entry.id}`}>
                        <td className="p-3 font-medium">${parseFloat(entry.rate).toFixed(2)}</td>
                        <td className="p-3 capitalize">{(entry.frequency || '').replace('_', ' ')}</td>
                        <td className="p-3">{formatDate(entry.start_date)}</td>
                        <td className="p-3">{entry.end_date ? formatDate(entry.end_date) : <Badge className="bg-green-100 text-green-800 text-xs">Current</Badge>}</td>
                        <td className="p-3 text-muted-foreground">{entry.notes || '-'}</td>
                        <td className="p-3"><button onClick={() => deleteCompensation(entry.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No charge history recorded</p>
            )}
          </Card>

          {/* Custom Fields for Benefits Tab */}
          {renderCustomFields('benefits')}
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training">
          <Card className="p-6">
            <h3 className="font-medium mb-4">Training Records</h3>
            <p className="text-center text-muted-foreground py-4">
              Training records feature coming soon
            </p>
          </Card>
        </TabsContent>

        {/* Pay Tab */}
        <TabsContent value="pay">
          <Card className="p-6" data-testid="pay-info-card">
            <h3 className="font-medium mb-4 flex items-center gap-2"><DollarSign size={16} /> Current Pay</h3>
            {isEditing ? (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Pay Rate</label>
                  <Input value={editedPerson.pay_rate || ''} onChange={(e) => setEditedPerson({ ...editedPerson, pay_rate: e.target.value })} placeholder="e.g., $50/visit or $75,000/year" />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Pay Type</label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editedPerson.pay_type || ''} onChange={(e) => setEditedPerson({ ...editedPerson, pay_type: e.target.value })}>
                    <option value="">Select...</option>
                    <option value="Hourly">Hourly</option>
                    <option value="Per Visit">Per Visit</option>
                    <option value="Salary">Salary</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2 h-10 mt-6 cursor-pointer">
                    <input type="checkbox" checked={editedPerson.bonus_eligible || false} onChange={(e) => setEditedPerson({ ...editedPerson, bonus_eligible: e.target.checked })} className="rounded" />
                    <span className="text-sm">Bonus Eligible</span>
                  </label>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                <div><p className="text-xs text-muted-foreground">Pay Rate</p><p className="text-sm font-medium">{person.pay_rate || '-'}</p></div>
                <div><p className="text-xs text-muted-foreground">Pay Type</p><p className="text-sm font-medium">{person.pay_type || '-'}</p></div>
                <div><p className="text-xs text-muted-foreground">Bonus Eligible</p><p className="text-sm font-medium">{person.bonus_eligible ? 'Yes' : 'No'}</p></div>
              </div>
            )}
          </Card>
          <p className="text-xs text-muted-foreground mt-2 ml-1">For full compensation, bonus, and commission history, see the <strong>Job</strong> tab.</p>
          {renderCustomFields('pay')}
        </TabsContent>

        {/* Onboarding Tab */}
        <TabsContent value="onboarding">
          <Card className="p-6" data-testid="onboarding-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium flex items-center gap-2"><ClipboardList size={16} /> Onboarding</h3>
              {onboardingData && onboardingData.status !== 'Not Started' && (
                <div className="flex items-center gap-3">
                  <Badge className={onboardingData.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                    {onboardingData.status}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {onboardingData.completed_items}/{onboardingData.total_items} items ({onboardingData.progress_percent}%)
                  </span>
                </div>
              )}
            </div>

            {/* Build onboarding (staff): apply a preset or add custom items with assignees */}
            <div className="mb-4 p-4 rounded-lg border border-dashed border-primary/30 bg-primary/5 space-y-3" data-testid="onboarding-builder">
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs font-medium text-muted-foreground">Apply a preset</label>
                  <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={applyPackId} onChange={e => setApplyPackId(e.target.value)} data-testid="onboarding-preset-select">
                    <option value="">Select a preset…</option>
                    {onboardingPacks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <Button size="sm" onClick={handleApplyPack} disabled={!applyPackId} data-testid="apply-preset-btn">Apply preset</Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddItem(v => !v)} data-testid="toggle-add-item-btn">
                  <Plus size={14} className="mr-1" /> {showAddItem ? 'Close' : 'Add custom item'}
                </Button>
              </div>

              {showAddItem && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t" data-testid="add-item-form">
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium">Item name *</label>
                    <Input className="h-9 mt-1" value={newOnbItem.name} placeholder="e.g., Upload Professional License"
                      onChange={e => setNewOnbItem(p => ({ ...p, name: e.target.value }))} data-testid="new-item-name" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Type</label>
                    <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={newOnbItem.item_type}
                      onChange={e => setNewOnbItem(p => ({ ...p, item_type: e.target.value }))} data-testid="new-item-type">
                      <option value="task">Task</option>
                      <option value="document">Document / Upload</option>
                      <option value="signature">Signature</option>
                      <option value="acknowledgment">Acknowledgment</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Assign to</label>
                    <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={newOnbItem.assignee_type}
                      onChange={e => setNewOnbItem(p => ({ ...p, assignee_type: e.target.value }))} data-testid="new-item-assignee-type">
                      <option value="new_hire">New hire</option>
                      <option value="user">Specific staff member</option>
                      <option value="role">Role</option>
                    </select>
                  </div>
                  {newOnbItem.assignee_type === 'user' && (
                    <div>
                      <label className="text-xs font-medium">Staff member</label>
                      <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={newOnbItem.assignee_user_id}
                        onChange={e => setNewOnbItem(p => ({ ...p, assignee_user_id: e.target.value }))} data-testid="new-item-user">
                        <option value="">Select…</option>
                        {staffUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                      </select>
                    </div>
                  )}
                  {newOnbItem.assignee_type === 'role' && (
                    <div>
                      <label className="text-xs font-medium">Role</label>
                      <Input className="h-9 mt-1" value={newOnbItem.assignee_role} placeholder="e.g., HR, Manager"
                        onChange={e => setNewOnbItem(p => ({ ...p, assignee_role: e.target.value }))} data-testid="new-item-role" />
                    </div>
                  )}
                  {newOnbItem.item_type === 'signature' && (
                    <div>
                      <label className="text-xs font-medium">Signature template</label>
                      <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={newOnbItem.esign_template_id}
                        onChange={e => setNewOnbItem(p => ({ ...p, esign_template_id: e.target.value }))} data-testid="new-item-esign">
                        <option value="">Select template…</option>
                        {esignTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                  )}
                  {newOnbItem.item_type === 'document' && (
                    <div>
                      <label className="text-xs font-medium">Credential type (optional)</label>
                      <Input className="h-9 mt-1" value={newOnbItem.credential_type} placeholder="e.g., Professional License"
                        onChange={e => setNewOnbItem(p => ({ ...p, credential_type: e.target.value }))} data-testid="new-item-credtype" />
                    </div>
                  )}
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2 pt-1 border-t">
                    <div>
                      <label className="text-xs font-medium">Due date</label>
                      <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={newOnbItem.due_mode}
                        onChange={e => setNewOnbItem(p => ({ ...p, due_mode: e.target.value }))} data-testid="new-item-due-mode">
                        <option value="none">No due date</option>
                        <option value="fixed">Fixed date</option>
                        <option value="before_hire">Days before hire</option>
                        <option value="after_hire">Days after hire</option>
                      </select>
                    </div>
                    {newOnbItem.due_mode === 'fixed' && (
                      <div>
                        <label className="text-xs font-medium">Date</label>
                        <Input type="date" className="h-9 mt-1" value={newOnbItem.due_date}
                          onChange={e => setNewOnbItem(p => ({ ...p, due_date: e.target.value }))} data-testid="new-item-due-date" />
                      </div>
                    )}
                    {(newOnbItem.due_mode === 'before_hire' || newOnbItem.due_mode === 'after_hire') && (
                      <div>
                        <label className="text-xs font-medium">Days {newOnbItem.due_mode === 'before_hire' ? 'before' : 'after'} hire</label>
                        <Input type="number" min="0" className="h-9 mt-1" value={newOnbItem.due_offset_days}
                          onChange={e => setNewOnbItem(p => ({ ...p, due_offset_days: e.target.value }))} data-testid="new-item-due-offset" />
                      </div>
                    )}
                    {newOnbItem.due_mode !== 'none' && (
                      <div>
                        <label className="text-xs font-medium">Reminder (days before)</label>
                        <Input type="number" min="0" placeholder="off" className="h-9 mt-1" value={newOnbItem.reminder_days_before}
                          onChange={e => setNewOnbItem(p => ({ ...p, reminder_days_before: e.target.value }))} data-testid="new-item-reminder" />
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2 flex items-center justify-between pt-1">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={newOnbItem.required} onChange={e => setNewOnbItem(p => ({ ...p, required: e.target.checked }))} /> Required
                    </label>
                    <Button size="sm" onClick={handleAddOnboardingItem} data-testid="save-new-item-btn">Add item</Button>
                  </div>
                </div>
              )}
            </div>

            {/* Not started - show start panel */}
            {(!onboardingData || onboardingData.status === 'Not Started' || (onboardingData.checklist && onboardingData.checklist.length === 0)) ? (
              <div className="text-center py-8 space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserPlus size={28} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-4">Pick the worker type and the onboarding preset you built, then start. The preset determines the exact tasks assigned.</p>
                  <div className="max-w-md mx-auto mb-4 text-left">
                    <label className="text-xs font-medium text-muted-foreground">Onboarding preset</label>
                    <select
                      className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1"
                      value={applyPackId}
                      onChange={e => setApplyPackId(e.target.value)}
                      data-testid="start-preset-select"
                    >
                      <option value="">Default items (no preset)</option>
                      {onboardingPacks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <button
                      onClick={() => setSelectedWorkerType('W2')}
                      className={`px-6 py-3 rounded-lg border-2 transition-all ${selectedWorkerType === 'W2' ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-border hover:border-primary/50'}`}
                      data-testid="worker-type-w2"
                    >
                      <div className="text-lg font-medium">W2</div>
                      <div className="text-xs text-muted-foreground">Employee</div>
                    </button>
                    <button
                      onClick={() => setSelectedWorkerType('1099')}
                      className={`px-6 py-3 rounded-lg border-2 transition-all ${selectedWorkerType === '1099' ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-border hover:border-primary/50'}`}
                      data-testid="worker-type-1099"
                    >
                      <div className="text-lg font-medium">1099</div>
                      <div className="text-xs text-muted-foreground">Contractor</div>
                    </button>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button onClick={handleStartOnboarding} disabled={startingOnboarding} data-testid="start-onboarding-btn">
                      <ClipboardList size={16} className="mr-2" />
                      {startingOnboarding ? 'Starting...' : 'Start Onboarding'}
                    </Button>
                    {onboardingTemplates.length === 0 && !applyPackId && (
                      <Button variant="outline" size="sm" onClick={handleSeedTemplates} data-testid="seed-templates-btn">
                        Seed Default Items
                      </Button>
                    )}
                  </div>
                  {/* Send invitation option */}
                  <label className="flex items-center justify-center gap-2 mt-3 cursor-pointer" data-testid="send-invitation-toggle">
                    <input type="checkbox" checked={sendInvitation} onChange={(e) => setSendInvitation(e.target.checked)} className="rounded" />
                    <span className="text-sm">Send invitation email with portal login</span>
                  </label>
                  {applyPackId ? (
                    <p className="text-xs text-muted-foreground mt-2">
                      {(onboardingPacks.find(p => p.id === applyPackId)?.items || []).length} items from "{onboardingPacks.find(p => p.id === applyPackId)?.name}" will be assigned.
                    </p>
                  ) : (
                    onboardingTemplates.length > 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        No preset selected — {onboardingTemplates.filter(t => t.worker_types?.includes(selectedWorkerType)).length} generic default items will be assigned. Select your preset above to use your own checklist.
                      </p>
                    )
                  )}
                </div>
              </div>
            ) : (
              /* Active onboarding checklist */
              <div className="space-y-4">
                {/* Progress bar */}
                <div className="w-full bg-muted rounded-full h-2.5">
                  <div className="bg-primary h-2.5 rounded-full transition-all duration-500" style={{ width: `${onboardingData.progress_percent || 0}%` }} data-testid="onboarding-progress-bar" />
                </div>
                <p className="text-xs text-muted-foreground">Worker Type: <strong>{onboardingData.worker_type || '-'}</strong></p>

                {/* Checklist */}
                <div className="space-y-2">
                  {(onboardingData.checklist || []).map((item) => (
                    <div key={item.id} className={`p-3 rounded-lg border transition-colors ${item.overdue ? 'bg-red-50 border-red-200' : item.status === 'completed' ? 'bg-green-50 border-green-200' : item.status === 'in_progress' ? 'bg-blue-50 border-blue-200' : 'bg-background border-border'}`} data-testid={`onboarding-item-${item.id}`}>
                     <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleUpdateOnboardingItem(item.id, item.status === 'completed' ? 'pending' : 'completed')}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${item.status === 'completed' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-primary'}`}
                          data-testid={`toggle-item-${item.id}`}
                        >
                          {item.status === 'completed' && <CheckCircle size={12} />}
                        </button>
                        <div>
                          <p className={`text-sm font-medium ${item.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{item.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <Badge className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground">{item.item_type}</Badge>
                            {item.required && <Badge className="text-[10px] px-1.5 py-0 bg-red-50 text-red-600">Required</Badge>}
                            {(item.assignee_type || 'new_hire') === 'new_hire'
                              ? <Badge className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700">New hire</Badge>
                              : <Badge className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700" title="Hidden from the new hire's portal">
                                  Internal · {item.assignee_type === 'user' ? (item.assignee_user_name || 'Staff') : (item.assignee_role || 'Role')}
                                </Badge>}
                            {item.due_at && item.status !== 'completed' && (
                              item.overdue
                                ? <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700" data-testid={`overdue-badge-${item.id}`}>Overdue · {formatDate(item.due_at)}</Badge>
                                : <Badge className="text-[10px] px-1.5 py-0 bg-orange-50 text-orange-700">Due {formatDate(item.due_at)}</Badge>
                            )}
                            {item.completed_at && <span className="text-[10px] text-muted-foreground">Completed {formatDate(item.completed_at)}</span>}
                            {item.first_opened_at && !item.completed_at && <span className="text-[10px] text-blue-500">Viewed {formatDate(item.first_opened_at)}</span>}
                            {item.acknowledged_at && <span className="text-[10px] text-green-600">Acknowledged {formatDate(item.acknowledged_at)}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openScheduleEditor(item)} className="text-xs h-7" data-testid={`due-date-btn-${item.id}`} title="Set due date">
                          <Calendar size={12} />
                        </Button>
                        {item.item_type === 'signature' && item.esign_template_id && item.status !== 'completed' && (
                          <>
                            {['sent', 'in_progress'].includes(String(item.esign_status || '').toLowerCase()) ? (
                              <span className="text-[11px] px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid={`awaiting-signature-${item.id}`} title="One active signature request exists for this document">
                                Awaiting signature
                              </span>
                            ) : (
                              <Button size="sm" variant="ghost" disabled={signingBusyId === item.id} onClick={() => handleSendOnboardingSignature(item.id)} className="text-xs h-7 text-primary" data-testid={`send-signature-${item.id}`}>
                                <Send size={12} className="mr-1" /> {signingBusyId === item.id ? 'Sending…' : (item.esign_request_id ? 'Resend' : 'Send to sign')}
                              </Button>
                            )}
                          </>
                        )}
                        {item.status !== 'completed' && item.item_type !== 'signature' && item.item_type !== 'acknowledgment' && (
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateOnboardingItem(item.id, 'completed')} className="text-xs h-7" data-testid={`complete-item-${item.id}`}>
                            <CheckCircle size={12} className="mr-1" /> Done
                          </Button>
                        )}
                        {item.status !== 'completed' && (item.item_type === 'signature' || item.item_type === 'acknowledgment') && (
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateOnboardingItem(item.id, 'acknowledged')} className="text-xs h-7 text-green-700" data-testid={`acknowledge-item-${item.id}`}>
                            <Shield size={12} className="mr-1" /> Acknowledge
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteOnboardingItem(item.id)} className="text-xs h-7 text-destructive" data-testid={`delete-onb-item-${item.id}`}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                     </div>
                     {item.item_type === 'signature' && item.signer_progress?.length > 0 && (
                       <div className="mt-2 pl-8 space-y-1" data-testid={`signer-progress-${item.id}`}>
                         {(() => {
                           const seq = item.signing_order_type === 'sequential';
                           const firstPendingOrder = seq
                             ? Math.min(...item.signer_progress.filter(s => s.status !== 'signed').map(s => s.order ?? 0).concat([Infinity]))
                             : null;
                           return item.signer_progress.map((s, i) => {
                             const signed = s.status === 'signed';
                             const declined = s.status === 'declined';
                             const isTurn = seq && !signed && !declined && s.order === firstPendingOrder;
                             return (
                               <div key={i} className="flex items-center gap-2 text-xs">
                                 {signed ? <CheckCircle size={13} className="text-green-600" />
                                   : declined ? <XCircle size={13} className="text-red-500" />
                                   : <Clock size={13} className={isTurn ? 'text-amber-500' : 'text-muted-foreground'} />}
                                 <span className="text-muted-foreground">{s.role}:</span>
                                 <span className={signed ? 'text-green-700' : 'font-medium'}>{s.name}</span>
                                 <span className={`ml-1 ${signed ? 'text-green-600' : declined ? 'text-red-500' : isTurn ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                   {signed ? `✓ signed${s.signed_at ? ' ' + formatDate(s.signed_at) : ''}` : declined ? 'declined' : isTurn ? '⏳ waiting on them' : 'pending'}
                                 </span>
                               </div>
                             );
                           });
                         })()}
                       </div>
                     )}
                     {scheduleItemId === item.id && (
                       <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2" data-testid={`schedule-editor-${item.id}`}>
                         <select value={scheduleForm.due_mode} onChange={e => setScheduleForm(f => ({ ...f, due_mode: e.target.value }))} className="h-8 text-sm border rounded-md px-2" data-testid="schedule-mode-select">
                           <option value="none">No due date</option>
                           <option value="fixed">Fixed date</option>
                           <option value="before_hire">Days before hire date</option>
                           <option value="after_hire">Days after hire date</option>
                         </select>
                         {scheduleForm.due_mode === 'fixed' && (
                           <Input type="date" value={scheduleForm.due_date} onChange={e => setScheduleForm(f => ({ ...f, due_date: e.target.value }))} className="h-8 text-sm w-40" data-testid="schedule-date-input" />
                         )}
                         {(scheduleForm.due_mode === 'before_hire' || scheduleForm.due_mode === 'after_hire') && (
                           <span className="flex items-center gap-1 text-sm">
                             <Input type="number" min="0" value={scheduleForm.due_offset_days} onChange={e => setScheduleForm(f => ({ ...f, due_offset_days: e.target.value }))} className="h-8 text-sm w-20" data-testid="schedule-offset-input" />
                             days {scheduleForm.due_mode === 'before_hire' ? 'before' : 'after'} hire
                           </span>
                         )}
                         {scheduleForm.due_mode !== 'none' && (
                           <span className="flex items-center gap-1 text-sm">
                             reminder
                             <Input type="number" min="0" placeholder="off" value={scheduleForm.reminder_days_before} onChange={e => setScheduleForm(f => ({ ...f, reminder_days_before: e.target.value }))} className="h-8 text-sm w-20" data-testid="schedule-reminder-input" />
                             days before
                           </span>
                         )}
                         <Button size="sm" onClick={() => handleSaveSchedule(item.id)} data-testid={`save-schedule-${item.id}`}>Save</Button>
                         <Button size="sm" variant="ghost" onClick={() => setScheduleItemId(null)}>Cancel</Button>
                       </div>
                     )}
                    </div>
                  ))}
                </div>

                {/* Reset option */}
                <div className="pt-4 border-t flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {onboardingData.status === 'Complete' ? 'All required items have been completed.' : `${onboardingData.total_items - onboardingData.completed_items} items remaining.`}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm('Reset onboarding? This will clear all progress.')) return;
                      try {
                        const token = localStorage.getItem('token');
                        await axios.post(`${API}/personnel/${personnelId}/onboarding/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Onboarding reset');
                        fetchOnboardingData();
                        fetchPerson();
                      } catch(e) { toast.error(e.response?.data?.detail || 'Failed to reset'); }
                    }}
                    data-testid="reset-onboarding-btn"
                  >
                    Reset Onboarding
                  </Button>
                </div>
              </div>
            )}
          </Card>
          {renderCustomFields('onboarding')}
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule">
          <ScheduleSection
            relatedType="personnel"
            relatedId={personnelId}
            defaultAttendee={person ? {
              kind: 'personnel',
              ref_id: person.id,
              name: `${person.first_name || ''} ${person.last_name || ''}`.trim(),
              email: person.work_email || person.email || null,
            } : null}
            title={`Schedule for ${person?.first_name || ''}`.trim()}
          />
        </TabsContent>

        {/* Account Tab - Promote to User */}
        <TabsContent value="account">
          <PersonnelPromoteToUser 
            personnelId={personnelId} 
            person={person}
            onUpdate={fetchPerson}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Layout: Add Field button */}
      {editingLayout && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button 
            onClick={() => setShowAddFieldModal(true)} 
            className="shadow-lg rounded-full px-6"
            data-testid="add-field-fab"
          >
            <Plus size={16} className="mr-2" /> Add Custom Field
          </Button>
        </div>
      )}

      {/* Add Custom Field Modal */}
      {/* State-board license verification review dialog */}
      <Dialog open={verifyOpen} onOpenChange={(o) => { if (!o) dismissVerification(); }}>
        <DialogContent className="sm:max-w-2xl" data-testid="license-verify-modal">
          <DialogHeader>
            <DialogTitle>Verify {verifyType} with State Board</DialogTitle>
            <DialogDescription>
              {verifyRec?.status === 'processing' || verifyBusy ? 'Looking up the license on the state board…'
                : verifyRec?.status === 'needs_review' ? 'Review the board result, then confirm to file it.'
                : verifyRec?.status === 'no_result' ? 'The board returned no matching record.'
                : verifyRec?.status === 'unsupported' ? 'Automated lookup not available for this state/discipline yet.'
                : verifyRec?.status === 'error' ? 'The lookup could not be completed.'
                : ''}
            </DialogDescription>
          </DialogHeader>

          {(verifyRec?.status === 'processing' || verifyBusy) && (
            <div className="flex items-center gap-3 py-8 justify-center text-muted-foreground">
              <Loader2 className="animate-spin" size={20} /> Checking the state board…
            </div>
          )}

          {verifyRec?.status === 'unsupported' && (
            <p className="text-sm text-amber-600 py-2" data-testid="verify-unsupported">{verifyRec.error}</p>
          )}
          {verifyRec?.status === 'error' && (
            <p className="text-sm text-red-600 py-2" data-testid="verify-error">{verifyRec.error || 'Lookup failed.'}</p>
          )}
          {verifyRec?.status === 'no_result' && (
            <p className="text-sm py-2" data-testid="verify-noresult">No matching license was found for this last name + date of birth. Double-check the therapist's DOB and last name.</p>
          )}

          {verifyRec?.status === 'needs_review' && (
            <div className="space-y-4" data-testid="verify-review">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Status found:</span> <strong>{verifyRec.parsed_status || '—'}</strong></div>
                <div><span className="text-muted-foreground">Name on record:</span> <strong>{verifyRec.parsed_name || '—'}</strong></div>
                <div><span className="text-muted-foreground">License #:</span> <strong>{verifyRec.parsed_license_number || '—'}</strong></div>
                <div><span className="text-muted-foreground">Detected expiration:</span> <strong>{verifyRec.parsed_expiration || '—'}</strong></div>
              </div>
              <div>
                <Label htmlFor="verify-exp" className="text-sm">Expiration date to file</Label>
                <Input id="verify-exp" type="date" value={verifyExp}
                  onChange={(e) => setVerifyExp(e.target.value)}
                  className="w-48 mt-1" data-testid="verify-expiration-input" />
                <p className="text-xs text-muted-foreground mt-1">Confirm or correct the expiration date from the screenshot below.</p>
              </div>
              {verifyRec.screenshot_path && (
                <div className="border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                  <img src={`${API}/license-verifications/${verifyRec.id}/screenshot?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                    alt="Board result" className="w-full" data-testid="verify-screenshot" />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={dismissVerification} data-testid="verify-dismiss-btn">
              {verifyRec?.status === 'needs_review' ? 'Cancel' : 'Close'}
            </Button>
            {verifyRec?.status === 'needs_review' && (
              <Button onClick={confirmAndFile} disabled={verifyBusy} data-testid="verify-confirm-btn">
                {verifyBusy ? <Loader2 className="animate-spin mr-2" size={14} /> : <ShieldCheck className="mr-2" size={14} />}
                Confirm &amp; File
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddFieldModal} onOpenChange={setShowAddFieldModal}>
        <DialogContent className="sm:max-w-md" data-testid="add-field-modal">
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
            <DialogDescription>Create a new field that will appear on this personnel record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Field Name *</Label>
              <Input
                data-testid="add-field-name"
                value={newFieldForm.name}
                onChange={e => setNewFieldForm({ ...newFieldForm, name: e.target.value })}
                placeholder="e.g., License Number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={newFieldForm.field_type} onValueChange={v => setNewFieldForm({ ...newFieldForm, field_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="boolean">Yes/No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tab</Label>
                <Select value={newFieldForm.target_tab} onValueChange={v => setNewFieldForm({ ...newFieldForm, target_tab: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="job">Job</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="benefits">Benefits</SelectItem>
                    <SelectItem value="pay">Pay</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={newFieldForm.description}
                onChange={e => setNewFieldForm({ ...newFieldForm, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFieldModal(false)}>Cancel</Button>
            <Button onClick={handleAddCustomField} data-testid="add-field-submit">
              <Plus size={14} className="mr-2" /> Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduleEventModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        relatedType="personnel"
        relatedId={personnelId}
        defaultAttendee={person ? {
          kind: 'personnel',
          ref_id: person.id,
          name: `${person.first_name || ''} ${person.last_name || ''}`.trim(),
          email: person.work_email || person.email || null,
        } : null}
        onSaved={() => setShowScheduleModal(false)}
      />

      <DocumentPreviewModal
        open={previewingDoc !== null}
        onClose={() => setPreviewingDoc(null)}
        previewUrl={previewingDoc?.previewUrl}
        downloadUrl={previewingDoc?.downloadUrl}
        fileName={previewingDoc?.fileName}
        title={previewingDoc?.title}
      />

      <EditCredentialModal
        open={editingDocId !== null}
        documentId={editingDocId}
        onClose={() => setEditingDocId(null)}
        onSaved={() => { setEditingDocId(null); if (person?.id) fetchPersonnelCredentials(person.id); }}
      />

      <ComposeModal
        open={composePrefill !== null}
        onClose={() => setComposePrefill(null)}
        onSent={() => setComposePrefill(null)}
        mode="new"
        initialTo={composePrefill?.to || ''}
        initialSubject={composePrefill?.subject || ''}
        initialBody={composePrefill?.body || ''}
      />

      <SmsComposeModal
        open={smsTo !== null}
        onClose={() => setSmsTo(null)}
        onSent={() => setSmsTo(null)}
        toNumber={smsTo || ''}
        recipientName={person ? `${person.first_name || ''} ${person.last_name || ''}`.trim() : ''}
      />

      <ComposeModal
        open={emailAttachPrefill !== null}
        onClose={() => setEmailAttachPrefill(null)}
        onSent={() => setEmailAttachPrefill(null)}
        mode="new"
        attachDocumentIds={emailAttachPrefill?.document_ids || null}
        attachLabel={emailAttachPrefill?.label || ''}
      />

      {/* Change History modal (admin-only) */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="history-modal">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <History size={20} className="text-muted-foreground" />
                <div>
                  <h2 className="text-lg font-semibold">Change History</h2>
                  <p className="text-xs text-muted-foreground">Who changed this record and when — revert to any prior version</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)} data-testid="history-close">
                <X size={18} />
              </Button>
            </div>
            <div className="overflow-y-auto p-5">
              {historyLoading ? (
                <div className="py-12 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                </div>
              ) : historyList.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground" data-testid="history-empty">
                  No changes recorded yet. Every future edit will be logged here and can be reverted.
                </div>
              ) : (
                <div className="space-y-2">
                  {historyList.map((h) => (
                    <div
                      key={h.id}
                      className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/30"
                      data-testid={`history-entry-${h.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium">
                          {h.snapshot?.first_name || ''} {h.snapshot?.last_name || ''}
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {h.source === 'self-service' ? 'self-service edit' : h.source === 'revert-backup' ? 'pre-revert backup' : h.source}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {h.changed_at && new Date(h.changed_at).toLocaleString()} · by {h.changed_by_name || h.changed_by || 'unknown'}
                        </div>
                        {Array.isArray(h.changed_fields) && h.changed_fields.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Changed: {h.changed_fields.join(', ')}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRevert(h.id)}
                        disabled={revertingId === h.id}
                        data-testid={`revert-${h.id}`}
                        className="shrink-0"
                      >
                        <RotateCcw size={14} className="mr-1" />
                        Revert
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
